import { useState, useEffect, useMemo, useCallback } from "react";

const diffColor = (d) => d == null ? "#9ca3af" : d > 0 ? "#16a34a" : d < 0 ? "#dc2626" : "#9ca3af";
const diffLabel = (d) => d == null ? "—" : d > 0 ? `▲ +${d.toFixed(2)}` : d < 0 ? `▼ ${d.toFixed(2)}` : "─";

const ETF_CONFIG = {
  "00981A": { label: "00981A", sublabel: "主動統一台股增長", file: "holdings_00981A.json", accent: "#2563eb" },
  "00403A": { label: "00403A", sublabel: "主動統一升級50",   file: "holdings_00403A.json", accent: "#7c3aed" },
  "00985A": { label: "00985A", sublabel: "野村臺灣增強50",    file: "holdings_00985A.json", accent: "#059669" },
  "00992A": { label: "00992A", sublabel: "主動群益科技創新",  file: "holdings_00992A.json", accent: "#e11d48" },
  "00400A": { label: "00400A", sublabel: "主動國泰動能高息",   file: "holdings_00400A.json", accent: "#0891b2" },
  "0050":   { label: "0050",   sublabel: "元大台灣50",        file: "holdings_0050.json",   accent: "#d97706" },
};

// ── 集中式資料快取 Hook ───────────────────────────────
// 所有頁籤共用同一份資料，避免重複 fetch
// allHistory: { [etfKey]: Day[] }  ← 完整歷史（TrendAnalysis / StockHistory 用）
// latestSnap: { [etfKey]: Day }    ← 僅最新一天（其他頁籤用）
function useEtfData() {
  const ETF_KEYS = Object.keys(ETF_CONFIG);
  const [allHistory, setAllHistory] = useState({});
  const [loading,    setLoading]    = useState(true);

  const fetchAll = useCallback(() => {
    Promise.all(
      ETF_KEYS.map(key =>
        fetch(`${process.env.PUBLIC_URL}/${ETF_CONFIG[key].file}?t=${Date.now()}`)
          .then(r => r.ok ? r.json() : [])
          .then(json => [key, Array.isArray(json) ? json : [json]])
          .catch(() => [key, []])
      )
    ).then(results => {
      setAllHistory(Object.fromEntries(results));
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchAll]);

  // latestSnap：每檔 ETF 取最後一筆（日期最新）
  const latestSnap = useMemo(() => {
    const out = {};
    ETF_KEYS.forEach(k => {
      const days = allHistory[k] || [];
      out[k] = days[days.length - 1] || null;
    });
    return out;
  }, [allHistory]);

  return { allHistory, latestSnap, loading };
}




// ── 健康狀態 Badge ────────────────────────────────────
function HealthBadge() {
  const [health,   setHealth]   = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const load = useCallback(() => {
    fetch(`${process.env.PUBLIC_URL}/health.json?t=${Date.now()}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        // 防呆：確保格式正確才渲染
        if (data && typeof data === 'object' && data.etfs) {
          setHealth(data);
        } else {
          setHealth(null);
        }
      })
      .catch(() => setHealth(null));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  if (!health) return null;

  // 只顯示目前追蹤的 ETF（過濾已移除的）
  const etfEntries = Object.values(health.etfs || {}).filter(e => Object.keys(ETF_CONFIG).includes(e.code));
  const allOk  = etfEntries.every(e => e.status === "ok");
  const etfs   = etfEntries;
  const stale  = etfs.filter(e => e.status === "stale").map(e => e.code);
  const missing = etfs.filter(e => e.status === "missing").map(e => e.code);

  const color   = allOk ? "#16a34a" : "#d97706";
  const bgColor = allOk ? "#f0fdf4" : "#fffbeb";
  const border  = allOk ? "#bbf7d0" : "#fde68a";
  const label   = allOk ? "資料正常" : `${[...stale,...missing].join("、")} 未更新`;

  return (
    <div style={{ position:"relative", display:"inline-block" }}
      onMouseEnter={() => setShowDetail(true)}
      onMouseLeave={() => setShowDetail(false)}>
      <div title={`最後檢查：${health.checked_at?.slice(0,16)}`}
        style={{ display:"flex", alignItems:"center", gap:5, background:bgColor, border:`1px solid ${border}`,
          borderRadius:6, padding:"4px 9px", fontSize:11, cursor:"default" }}>
        <span style={{ color, fontSize:8 }}>●</span>
        <span style={{ color, fontWeight:500 }}>{label}</span>
      </div>
      {/* Hover 詳情 */}
      {showDetail && (
        <div style={{ position:"absolute", right:0, top:"calc(100% + 6px)",
          background:"white", border:"1px solid #e5e7eb", borderRadius:8, boxShadow:"0 4px 12px rgba(0,0,0,0.08)",
          zIndex:30, minWidth:260, padding:"10px 12px" }}>
          <div style={{ fontSize:11, color:"#9ca3af", marginBottom:8 }}>今日：{health.today}</div>
          {etfEntries.map(e => (
            <div key={e.code} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"4px 0", borderBottom:"1px solid #f9fafb" }}>
              <span style={{ fontFamily:"monospace", fontSize:12, fontWeight:600,
                color: e.status==="ok"?"#16a34a":e.status==="stale"?"#d97706":"#dc2626" }}>
                {e.status==="ok"?"✓":e.status==="stale"?"⚠":"✗"} {e.code}
              </span>
              <span style={{ fontSize:11, color:"#6b7280" }}>{e.message}</span>
            </div>
          ))}
          <div style={{ fontSize:10, color:"#d1d5db", marginTop:8 }}>
            更新於 {health.checked_at?.slice(11,16)}
          </div>
        </div>
      )}
    </div>
  );
}

function ETFDashboard({ etfKey }) {
  const cfg = ETF_CONFIG[etfKey];
  const [history,    setHistory]    = useState([]);
  const [dayIdx,     setDayIdx]     = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [search,     setSearch]     = useState("");
  const [sortBy,     setSortBy]     = useState("weight");
  const [minWeight,  setMinWeight]  = useState(0);
  const [diffFilter, setDiffFilter] = useState("all");
  const [selected,   setSelected]   = useState(null);
  const [fetchedAt,  setFetchedAt]  = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIdx,  setCompareIdx]  = useState(null);  // 比較基準日

  const loadData = useCallback(() => {
    setLoading(true);
    fetch(`${process.env.PUBLIC_URL}/${cfg.file}?t=${Date.now()}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(json => {
        setHistory([...(Array.isArray(json) ? json : [json])].reverse());
        setFetchedAt(new Date());
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [cfg.file]);

  useEffect(() => {
    loadData();
    const t = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [loadData]);

  // 關閉比較模式時重置
  useEffect(() => {
    if (!compareMode) setCompareIdx(null);
  }, [compareMode]);

  const current = history[dayIdx] || {};
  const compareDay = compareMode && compareIdx != null ? (history[compareIdx] || null) : null;

  // 自選日期比較：動態計算 diff（compareDay → current）
  const customDiff = useMemo(() => {
    if (!compareDay) return null;
    const baseMap = Object.fromEntries((compareDay.stocks || []).map(s => [s.code, s.weight]));
    const baseSet = new Set(Object.keys(baseMap));
    const currSet = new Set((current.stocks || []).map(s => s.code));
    const result = {};
    (current.stocks || []).forEach(s => {
      result[s.code] = {
        diff:   baseMap[s.code] != null ? +(s.weight - baseMap[s.code]).toFixed(3) : null,
        is_new: !baseSet.has(s.code),
      };
    });
    // 消失的股票
    const removed_custom = [];
    baseSet.forEach(code => {
      if (!currSet.has(code)) {
        const s = compareDay.stocks.find(x => x.code === code);
        removed_custom.push({ ...s, diff: -s.weight, removed: true });
      }
    });
    return { result, removed_custom };
  }, [compareDay, current]);
  const stocks  = useMemo(() => {
    if (!customDiff) return current.stocks || [];
    return (current.stocks || []).map(s => ({
      ...s,
      diff:   customDiff.result[s.code]?.diff   ?? null,
      is_new: customDiff.result[s.code]?.is_new ?? false,
    }));
  }, [current, customDiff]);
  const hasDiff = compareDay ? true : stocks.some(s => s.diff != null);
  const removed = compareDay ? (customDiff?.removed_custom || []) : (current.removed_stocks || []);

  const filtered = useMemo(() => {
    let list = stocks.filter(s => {
      const q = search.trim();
      return (!q || s.name.includes(q) || s.code.includes(q))
        && s.weight >= minWeight
        && (diffFilter === "all" ? true
          : diffFilter === "up"   ? s.diff > 0
          : diffFilter === "down" ? s.diff < 0
          : s.is_new);
    });
    if (sortBy === "weight")   return [...list].sort((a,b) => b.weight - a.weight);
    if (sortBy === "diff")     return [...list].sort((a,b) => (b.diff||0) - (a.diff||0));
    if (sortBy === "diff_abs") return [...list].sort((a,b) => Math.abs(b.diff||0) - Math.abs(a.diff||0));
    if (sortBy === "code")     return [...list].sort((a,b) => a.code.localeCompare(b.code));
    return list;
  }, [stocks, search, sortBy, minWeight, diffFilter]);

  const sel = selected ? stocks.find(s => s.code === selected) : null;
  const top5  = stocks.slice(0,5).reduce((a,s) => a + s.weight, 0).toFixed(2);
  const top10 = stocks.slice(0,10).reduce((a,s) => a + s.weight, 0).toFixed(2);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200 }}>
      <div style={{ width:24, height:24, border:`2px solid #e5e7eb`, borderTop:`2px solid ${cfg.accent}`, borderRadius:"50%", animation:"spin .7s linear infinite" }} />
    </div>
  );

  if (error) return (
    <div style={{ textAlign:"center", padding:40, color:"#dc2626", fontSize:13 }}>
      <p>{error}</p>
      <p style={{ color:"#9ca3af", marginTop:6, fontSize:12 }}>請確認 backend/download_{etfKey}.py 已執行</p>
      <button onClick={loadData} style={{ marginTop:12, border:"1px solid #dc2626", color:"#dc2626", background:"white", borderRadius:6, padding:"5px 14px", cursor:"pointer", fontSize:12 }}>重試</button>
    </div>
  );

  return (
    <div>
      {/* 日期切換 */}
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:compareMode ? 8 : 16, flexWrap:"wrap" }}>
        <span style={{ fontSize:11, color:"#9ca3af" }}>資料日期</span>
        {history.map((h,i) => (
          <button key={h.date}
            onClick={() => { setDayIdx(i); setSelected(null); if (compareMode && compareIdx === i) setCompareIdx(null); }}
            style={{ border:`1px solid ${dayIdx===i ? cfg.accent : "#e5e7eb"}`, borderRadius:5, padding:"3px 10px", fontSize:11, cursor:"pointer",
              background: dayIdx===i ? cfg.accent : "white", color: dayIdx===i ? "white" : "#6b7280", transition:"all .15s",
              outline: compareMode && compareIdx===i ? `2px solid #f59e0b` : "none", outlineOffset:2 }}>
            {h.date}
          </button>
        ))}
        <span style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8 }}>
          {/* 比較模式開關 */}
          <button onClick={() => setCompareMode(v => !v)}
            style={{ border:`1px solid ${compareMode ? "#f59e0b" : "#e5e7eb"}`, borderRadius:5, padding:"3px 10px", fontSize:11, cursor:"pointer",
              background: compareMode ? "#fffbeb" : "white", color: compareMode ? "#d97706" : "#6b7280", fontWeight: compareMode ? 600 : 400 }}>
            ⇄ 比較
          </button>
          <span style={{ fontSize:11, color:"#d1d5db" }}>{fetchedAt && fetchedAt.toLocaleTimeString("zh-TW")}</span>
          <button onClick={loadData} style={{ border:"none", background:"none", color:"#9ca3af", cursor:"pointer", fontSize:13 }}>↺</button>

        </span>
      </div>

      {/* 比較模式：選基準日 */}
      {compareMode && (
        <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <span style={{ color:"#d97706", fontWeight:600 }}>⇄ 比較基準日</span>
            <span style={{ color:"#9ca3af" }}>（橘框 = 基準日，藍底 = 觀察日）</span>
            {compareIdx == null
              ? <span style={{ color:"#d97706" }}>← 請點選一個日期作為基準</span>
              : <span style={{ color:"#16a34a", fontWeight:500 }}>
                  {history[compareIdx]?.date} → {current.date}
                  　新增 {(customDiff?.removed_custom ? stocks.filter(s => customDiff.result[s.code]?.is_new).length : 0)} 支
                  　移除 {customDiff?.removed_custom?.length || 0} 支
                </span>
            }
            {compareIdx != null && (
              <button onClick={() => setCompareIdx(null)}
                style={{ border:"1px solid #fde68a", borderRadius:4, padding:"2px 8px", fontSize:11, cursor:"pointer", background:"white", color:"#d97706" }}>
                清除
              </button>
            )}
          </div>
          {/* 基準日按鈕列 */}
          <div style={{ display:"flex", gap:5, marginTop:8, flexWrap:"wrap" }}>
            {history.map((h, i) => i === dayIdx ? null : (
              <button key={h.date} onClick={() => setCompareIdx(i)}
                style={{ border:`1px solid ${compareIdx===i ? "#f59e0b" : "#e5e7eb"}`, borderRadius:5, padding:"3px 10px", fontSize:11, cursor:"pointer",
                  background: compareIdx===i ? "#fef3c7" : "white", color: compareIdx===i ? "#d97706" : "#6b7280",
                  fontWeight: compareIdx===i ? 600 : 400 }}>
                {h.date}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 摘要 */}
      <div className="summary-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:8, marginBottom:16 }}>
        {[
          { label:"淨值",     val:`NT$${current.nav||"—"}` },
          { label:"前5大",    val:`${top5}%` },
          { label:"前10大",   val:`${top10}%` },
          { label:"持股數",   val:`${stocks.length} 支` },
          hasDiff && { label:"今日增加", val:`${stocks.filter(s=>s.diff>0).length} 支`, c:"#16a34a" },
          hasDiff && { label:"今日減少", val:`${stocks.filter(s=>s.diff<0).length} 支`, c:"#dc2626" },
        ].filter(Boolean).map(({ label, val, c }) => (
          <div key={label} style={{ border:"1px solid #f3f4f6", borderRadius:8, padding:"10px 12px", background:"#fafafa" }}>
            <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:15, fontWeight:600, color: c || cfg.accent }}>{val}</div>
          </div>
        ))}
      </div>

      {/* 選中詳情 */}
      {sel && (
        <div style={{ border:`1px solid ${cfg.accent}30`, borderRadius:8, padding:"12px 14px", marginBottom:14, background:`${cfg.accent}05` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
            <div>
              <span style={{ fontSize:11, color:"#9ca3af" }}>{sel.code} </span>
              <span style={{ fontSize:16, fontWeight:600, color:"#111827" }}>{sel.name}</span>
              {sel.is_new && <span style={{ fontSize:10, background:"#dcfce7", color:"#16a34a", borderRadius:4, padding:"1px 6px", marginLeft:6 }}>新增</span>}
            </div>
            <div style={{ display:"flex", gap:20 }}>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"#9ca3af" }}>持股比重</div>
                <div style={{ fontSize:18, fontWeight:700, color:cfg.accent }}>{sel.weight.toFixed(2)}%</div>
              </div>
              {hasDiff && sel.diff != null && (
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:10, color:"#9ca3af" }}>日變化</div>
                  <div style={{ fontSize:18, fontWeight:700, color:diffColor(sel.diff) }}>{diffLabel(sel.diff)}</div>
                </div>
              )}
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"#9ca3af" }}>排名</div>
                <div style={{ fontSize:18, fontWeight:700, color:"#6b7280" }}>#{stocks.findIndex(s=>s.code===sel.code)+1}</div>
              </div>
            </div>
          </div>
          <div style={{ marginTop:10, height:3, background:"#f3f4f6", borderRadius:2 }}>
            <div style={{ height:"100%", width:`${(sel.weight/stocks[0]?.weight)*100}%`, background:cfg.accent, borderRadius:2, transition:"width .4s" }} />
          </div>
        </div>
      )}

      {/* 控制列 */}
      <div className="filter-row" style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap", alignItems:"center" }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="搜尋代號或名稱"
          className="filter-search" style={{ border:"1px solid #e5e7eb", borderRadius:6, padding:"5px 10px", fontSize:12, width:140, outline:"none" }} />
        <select value={minWeight} onChange={e=>setMinWeight(Number(e.target.value))}
          style={{ border:"1px solid #e5e7eb", borderRadius:6, padding:"5px 8px", fontSize:11, color:"#6b7280", outline:"none" }}>
          <option value={0}>全部</option>
          <option value={0.5}>≥ 0.5%</option>
          <option value={1}>≥ 1%</option>
          <option value={2}>≥ 2%</option>
          <option value={5}>≥ 5%</option>
        </select>
        {hasDiff && (
          <div style={{ display:"flex", gap:4 }}>
            {[["all","全部"],["up","增加"],["down","減少"],["new","新增"]].map(([v,l]) => (
              <button key={v} onClick={()=>setDiffFilter(v)}
                style={{ border:"1px solid #e5e7eb", borderRadius:5, padding:"4px 9px", fontSize:11, cursor:"pointer", background: diffFilter===v ? "#f3f4f6" : "white", color: diffFilter===v ? "#111827" : "#6b7280" }}>{l}</button>
            ))}
          </div>
        )}
        <div style={{ display:"flex", gap:4 }}>
          {[["weight","權重"],["diff","變化"],["code","代號"]].map(([v,l]) => (
            <button key={v} onClick={()=>setSortBy(v)}
              style={{ border:"1px solid #e5e7eb", borderRadius:5, padding:"4px 9px", fontSize:11, cursor:"pointer", background: sortBy===v ? cfg.accent : "white", color: sortBy===v ? "white" : "#6b7280" }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize:11, color:"#d1d5db", marginLeft:"auto" }}>{filtered.length}/{stocks.length}</span>
      </div>

      {/* 表格 */}
      <div style={{ border:"1px solid #f3f4f6", borderRadius:8, overflow:"hidden" }}>
        <table className="holdings-table" style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"#fafafa", borderBottom:"1px solid #f3f4f6" }}>
              {["#","代號","名稱","持股比重", hasDiff?(compareDay?`↔ 變化`:"日變化"):null,"股數"].filter(Boolean).map((h,i) => (
                <th key={i} style={{ padding:"9px 12px", textAlign:i<=2?"left":"right", fontSize:11, color:"#9ca3af", fontWeight:500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const rank = stocks.findIndex(x=>x.code===s.code)+1;
              const isSel = selected===s.code;
              return (
                <tr key={s.code}
                  onClick={()=>setSelected(isSel?null:s.code)}
                  style={{ borderBottom:"1px solid #f9fafb", background: isSel ? `${cfg.accent}08` : "white", cursor:"pointer" }}
                  onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.background="#f9fafb" }}
                  onMouseLeave={e=>{ e.currentTarget.style.background=isSel?`${cfg.accent}08`:"white" }}>
                  <td className="td-rank" style={{ padding:"9px 12px", color:"#d1d5db", fontSize:11 }}>{rank}</td>
                  <td className="td-code" style={{ padding:"9px 12px", color:"#6b7280", fontSize:11, fontFamily:"monospace" }}>{s.code}</td>
                  <td className="td-name" style={{ padding:"9px 12px", color:"#111827", fontWeight: s.weight>=3?500:400 }}>
                    {s.name}
                    {rank<=5  && <span style={{ fontSize:9, background:`${cfg.accent}15`, color:cfg.accent, borderRadius:3, padding:"1px 5px", marginLeft:5 }}>TOP5</span>}
                    {rank>5&&rank<=10 && <span style={{ fontSize:9, background:"#f3f4f6", color:"#6b7280", borderRadius:3, padding:"1px 5px", marginLeft:5 }}>TOP10</span>}
                    {s.is_new && <span style={{ fontSize:9, background:"#dcfce7", color:"#16a34a", borderRadius:3, padding:"1px 5px", marginLeft:5 }}>NEW</span>}
                  </td>
                  <td className="td-weight" style={{ padding:"9px 12px", textAlign:"right", fontWeight:600, color:cfg.accent, fontFamily:"monospace" }}>{s.weight.toFixed(2)}%</td>
                  {hasDiff && (
                    <td className="td-diff" style={{ padding:"9px 12px", textAlign:"right", fontSize:12, fontWeight:500, color:diffColor(s.diff), fontFamily:"monospace" }}>{diffLabel(s.diff)}</td>
                  )}
                  <td className="td-shares" style={{ padding:"9px 12px", textAlign:"right", color:"#9ca3af", fontSize:11, fontFamily:"monospace" }}>{s.shares.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {removed.length > 0 && (
        <div style={{ marginTop:8, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:6, padding:"8px 12px", fontSize:12, color:"#dc2626" }}>
          本日移除：{removed.map(s=><span key={s.code} style={{ fontFamily:"monospace", marginLeft:6 }}>{s.code}</span>)}
        </div>
      )}
    </div>
  );
}

// ── 重疊分析元件 ─────────────────────────────────────
function OverlapAnalysis({ latestSnap, loading }) {
  const ETF_KEYS = Object.keys(ETF_CONFIG);
  const [minAppear, setMinAppear] = useState(2);
  const [search,    setSearch]    = useState("");
  const [sortBy,    setSortBy]    = useState("exposure");
  const [selected,  setSelected]  = useState(null);

  // 從 latestSnap 解出 allData / allDates（格式與舊版相同）
  const allData  = useMemo(() => Object.fromEntries(ETF_KEYS.map(k => [k, latestSnap[k]?.stocks || []])), [latestSnap]);
  const allDates = useMemo(() => Object.fromEntries(ETF_KEYS.map(k => [k, latestSnap[k]?.date   || ""])), [latestSnap]);

  // 建立重疊分析資料
  const overlapData = useMemo(() => {
    if (!Object.keys(allData).length) return [];

    const stockMap = {};
    ETF_KEYS.forEach(etfKey => {
      (allData[etfKey] || []).forEach(s => {
        if (!stockMap[s.code]) {
          stockMap[s.code] = { code: s.code, name: s.name, etfs: {}, count: 0 };
        }
        stockMap[s.code].etfs[etfKey] = s.weight;
        stockMap[s.code].count++;
      });
    });

    return Object.values(stockMap)
      .filter(s => s.count >= minAppear)
      .map(s => ({
        ...s,
        // 等比重加總曝險：各 ETF 各佔 25%，持股比重再乘以 ETF 佔比
        exposure: ETF_KEYS.reduce((sum, k) => sum + (s.etfs[k] || 0) * (1/ETF_KEYS.length), 0),
      }));
  }, [allData, minAppear]);

  const filtered = useMemo(() => {
    const q = search.trim();
    let list = overlapData.filter(s =>
      !q || s.name.includes(q) || s.code.includes(q)
    );
    if (sortBy === "exposure") return [...list].sort((a,b) => b.exposure - a.exposure);
    if (sortBy === "count")    return [...list].sort((a,b) => b.count - a.count);
    if (sortBy === "code")     return [...list].sort((a,b) => a.code.localeCompare(b.code));
    return list;
  }, [overlapData, search, sortBy]);

  const sel = selected ? filtered.find(s => s.code === selected) : null;
  const maxExposure = filtered[0]?.exposure || 1;

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200 }}>
      <div style={{ width:24, height:24, border:"2px solid #e5e7eb", borderTop:"2px solid #6366f1", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
    </div>
  );

  return (
    <div>
      {/* 說明 */}
      <div style={{ background:"#f9fafb", border:"1px solid #f3f4f6", borderRadius:8, padding:"12px 14px", marginBottom:16, fontSize:12, color:"#6b7280", lineHeight:1.6 }}>
        <strong style={{ color:"#111827" }}>等比重假設：</strong>每檔 ETF 各佔投資組合 {(100/ETF_KEYS.length).toFixed(0)}%。
        加總曝險 = Σ（各 ETF 持股比重 × {(1/ETF_KEYS.length).toFixed(2)}）。數值越高代表對該股票的實際曝險越大。
        <div style={{ marginTop:6, display:"flex", flexWrap:"wrap", gap:"4px 12px" }}>
          {ETF_KEYS.map(k => allDates[k] ? (
            <span key={k} style={{ fontSize:11 }}>
              <span style={{ color:ETF_CONFIG[k].accent, fontWeight:600 }}>{k}</span>
              <span style={{ color:"#9ca3af" }}> {allDates[k]}</span>
            </span>
          ) : null)}
        </div>
      </div>

      {/* 摘要 */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:8, marginBottom:16 }}>
        {[
          { label:"出現在 2+ 檔", val:`${overlapData.filter(s=>s.count>=2).length} 支`, c:"#6366f1" },
          { label:"出現在 3+ 檔", val:`${overlapData.filter(s=>s.count>=3).length} 支`, c:"#2563eb" },
          { label:`${ETF_KEYS.length} 檔都有`, val:`${overlapData.filter(s=>s.count>=ETF_KEYS.length).length} 支`, c:"#dc2626" },
          { label:"最高曝險",     val:`${filtered[0]?.exposure.toFixed(2) || "—"}%`, c:"#d97706" },
        ].map(({ label, val, c }) => (
          <div key={label} style={{ border:"1px solid #f3f4f6", borderRadius:8, padding:"10px 12px", background:"#fafafa" }}>
            <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:15, fontWeight:600, color:c }}>{val}</div>
          </div>
        ))}
      </div>

      {/* 選中股票詳情 */}
      {sel && (
        <div style={{ border:"1px solid #e0e7ff", borderRadius:8, padding:"12px 14px", marginBottom:14, background:"#f5f3ff" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:8 }}>
            <div>
              <span style={{ fontSize:11, color:"#9ca3af" }}>{sel.code} </span>
              <span style={{ fontSize:16, fontWeight:600 }}>{sel.name}</span>
              <span style={{ marginLeft:8, fontSize:11, background:"#e0e7ff", color:"#6366f1", borderRadius:4, padding:"1px 7px" }}>
                {sel.count} 檔持有
              </span>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:10, color:"#9ca3af" }}>加總曝險（等比重）</div>
              <div style={{ fontSize:20, fontWeight:700, color:"#6366f1" }}>{sel.exposure.toFixed(3)}%</div>
            </div>
          </div>
          {/* 各 ETF 持股比重 */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginTop:12 }}>
            {ETF_KEYS.map(k => {
              const w = sel.etfs[k];
              const cfg = ETF_CONFIG[k];
              return (
                <div key={k} style={{ background:"white", borderRadius:6, padding:"8px 10px", border:`1px solid ${w ? cfg.accent+"30" : "#f3f4f6"}` }}>
                  <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>{k}</div>
                  <div style={{ fontSize:14, fontWeight:700, color: w ? cfg.accent : "#d1d5db" }}>
                    {w ? `${w.toFixed(2)}%` : "—"}
                  </div>
                  {w && <div style={{ marginTop:6, height:3, background:"#f3f4f6", borderRadius:2 }}>
                    <div style={{ height:"100%", width:`${(w/20)*100}%`, maxWidth:"100%", background:cfg.accent, borderRadius:2 }} />
                  </div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 控制列 */}
      <div className="filter-row" style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap", alignItems:"center" }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="搜尋代號或名稱"
          style={{ border:"1px solid #e5e7eb", borderRadius:6, padding:"5px 10px", fontSize:12, width:140, outline:"none" }} />
        <div style={{ display:"flex", gap:4 }}>
          {[["2","2+ 檔"],["3","3+ 檔"],["4","4 檔"]].map(([v,l]) => (
            <button key={v} onClick={()=>setMinAppear(Number(v))}
              style={{ border:"1px solid #e5e7eb", borderRadius:5, padding:"4px 10px", fontSize:11, cursor:"pointer",
                background: minAppear===Number(v) ? "#6366f1" : "white",
                color:      minAppear===Number(v) ? "white"   : "#6b7280" }}>{l}</button>
          ))}
        </div>
        <div style={{ display:"flex", gap:4 }}>
          {[["exposure","曝險↓"],["count","出現次數"],["code","代號"]].map(([v,l]) => (
            <button key={v} onClick={()=>setSortBy(v)}
              style={{ border:"1px solid #e5e7eb", borderRadius:5, padding:"4px 10px", fontSize:11, cursor:"pointer",
                background: sortBy===v ? "#111827" : "white",
                color:      sortBy===v ? "white"   : "#6b7280" }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize:11, color:"#d1d5db", marginLeft:"auto" }}>{filtered.length} 支</span>
      </div>

      {/* 熱力表格 */}
      <div style={{ border:"1px solid #f3f4f6", borderRadius:8, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead>
            <tr style={{ background:"#fafafa", borderBottom:"1px solid #f3f4f6" }}>
              <th style={{ padding:"9px 12px", textAlign:"left",  fontSize:11, color:"#9ca3af", fontWeight:500 }}>代號</th>
              <th style={{ padding:"9px 12px", textAlign:"left",  fontSize:11, color:"#9ca3af", fontWeight:500 }}>名稱</th>
              {ETF_KEYS.map(k => (
                <th key={k} style={{ padding:"9px 10px", textAlign:"right", fontSize:11, color:ETF_CONFIG[k].accent, fontWeight:600 }}>{k}</th>
              ))}
              <th style={{ padding:"9px 12px", textAlign:"right", fontSize:11, color:"#6366f1", fontWeight:600 }}>加總曝險</th>
              <th style={{ padding:"9px 12px", textAlign:"center",fontSize:11, color:"#9ca3af", fontWeight:500 }}>出現</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const isSel = selected === s.code;
              return (
                <tr key={s.code}
                  onClick={() => setSelected(isSel ? null : s.code)}
                  style={{ borderBottom:"1px solid #f9fafb", cursor:"pointer", background: isSel ? "#f5f3ff" : "white" }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = "#fafafa"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isSel ? "#f5f3ff" : "white"; }}>
                  <td style={{ padding:"8px 12px", color:"#6b7280", fontFamily:"monospace", fontSize:11 }}>{s.code}</td>
                  <td style={{ padding:"8px 12px", color:"#111827", fontWeight: s.exposure>5?600:400 }}>{s.name}</td>
                  {ETF_KEYS.map(k => {
                    const w = s.etfs[k];
                    const intensity = w ? Math.min(w / 15, 1) : 0;
                    const bg = w ? `${ETF_CONFIG[k].accent}${Math.round(intensity * 30 + 8).toString(16).padStart(2,"0")}` : "transparent";
                    return (
                      <td key={k} style={{ padding:"8px 10px", textAlign:"right", fontFamily:"monospace",
                        color: w ? ETF_CONFIG[k].accent : "#e5e7eb", background: bg, fontSize:11, fontWeight: w?600:400 }}>
                        {w ? `${w.toFixed(2)}%` : "—"}
                      </td>
                    );
                  })}
                  <td style={{ padding:"8px 12px", textAlign:"right", fontFamily:"monospace", fontWeight:700, color:"#6366f1" }}>
                    {s.exposure.toFixed(3)}%
                  </td>
                  <td style={{ padding:"8px 12px", textAlign:"center" }}>
                    <span style={{
                      background: s.count===ETF_KEYS.length?"#fee2e2":s.count>=ETF_KEYS.length-1?"#fef3c7":"#eff6ff",
                      color:      s.count===ETF_KEYS.length?"#dc2626":s.count>=ETF_KEYS.length-1?"#d97706":"#2563eb",
                      borderRadius:4, padding:"2px 7px", fontSize:10, fontWeight:600 }}>
                      {s.count}/{ETF_KEYS.length}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── ETF 比較模式 ──────────────────────────────────────
function CompareMode({ latestSnap, loading }) {
  const ETF_KEYS = Object.keys(ETF_CONFIG);
  const [leftKey,  setLeftKey]  = useState("00981A");
  const [rightKey, setRightKey] = useState("0050");
  const [search,   setSearch]   = useState("");
  const [sortBy,   setSortBy]   = useState("left");

  const leftSnap  = latestSnap[leftKey]  || {};
  const rightSnap = latestSnap[rightKey] || {};
  const leftData  = leftSnap.stocks   || [];
  const rightData = rightSnap.stocks  || [];
  const leftMap   = Object.fromEntries(leftData.map(s => [s.code, s]));
  const rightMap  = Object.fromEntries(rightData.map(s => [s.code, s]));

  // 合併所有代碼
  const allCodes = useMemo(() => {
    const codes = new Set([...Object.keys(leftMap), ...Object.keys(rightMap)]);
    const q = search.trim();
    let rows = [...codes].map(code => ({
      code,
      name:  leftMap[code]?.name  || rightMap[code]?.name || "",
      left:  leftMap[code]?.weight  || 0,
      right: rightMap[code]?.weight || 0,
      inBoth: !!leftMap[code] && !!rightMap[code],
    })).filter(r => !q || r.name.includes(q) || r.code.includes(q));

    if (sortBy === "left")  return [...rows].sort((a,b) => b.left  - a.left);
    if (sortBy === "right") return [...rows].sort((a,b) => b.right - a.right);
    if (sortBy === "diff")  return [...rows].sort((a,b) => Math.abs(b.left - b.right) - Math.abs(a.left - a.right));
    if (sortBy === "both")  return rows.filter(r => r.inBoth).sort((a,b) => b.left - a.left);
    return rows;
  }, [leftMap, rightMap, search, sortBy]);

  const maxWeight = Math.max(...allCodes.map(r => Math.max(r.left, r.right)), 1);
  const leftCfg  = ETF_CONFIG[leftKey];
  const rightCfg = ETF_CONFIG[rightKey];

  // 共同持股數、獨有數
  const bothCount  = allCodes.filter(r => r.inBoth).length;
  const leftOnly   = allCodes.filter(r => r.left  > 0 && !r.inBoth).length;
  const rightOnly  = allCodes.filter(r => r.right > 0 && !r.inBoth).length;

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200 }}>
      <div style={{ width:24, height:24, border:"2px solid #e5e7eb", borderTop:"2px solid #ec4899", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
    </div>
  );

  return (
    <div>
      {/* ETF 選擇器 */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:140 }}>
          <div style={{ fontSize:10, color:"#9ca3af", marginBottom:4 }}>
            左側 ETF
            {leftSnap.date && <span style={{ marginLeft:6, color:"#d1d5db" }}>· {leftSnap.date}</span>}
          </div>
          <select value={leftKey} onChange={e => setLeftKey(e.target.value)}
            style={{ width:"100%", border:`2px solid ${leftCfg.accent}`, borderRadius:7, padding:"7px 10px", fontSize:13, fontWeight:600, color:leftCfg.accent, outline:"none", cursor:"pointer" }}>
            {ETF_KEYS.filter(k => k !== rightKey).map(k => (
              <option key={k} value={k}>{ETF_CONFIG[k].label} {ETF_CONFIG[k].sublabel}</option>
            ))}
          </select>
        </div>
        <div style={{ fontSize:18, color:"#d1d5db", paddingTop:18 }}>⇄</div>
        <div style={{ flex:1, minWidth:140 }}>
          <div style={{ fontSize:10, color:"#9ca3af", marginBottom:4 }}>
            右側 ETF
            {rightSnap.date && <span style={{ marginLeft:6, color:"#d1d5db" }}>· {rightSnap.date}</span>}
          </div>
          <select value={rightKey} onChange={e => setRightKey(e.target.value)}
            style={{ width:"100%", border:`2px solid ${rightCfg.accent}`, borderRadius:7, padding:"7px 10px", fontSize:13, fontWeight:600, color:rightCfg.accent, outline:"none", cursor:"pointer" }}>
            {ETF_KEYS.filter(k => k !== leftKey).map(k => (
              <option key={k} value={k}>{ETF_CONFIG[k].label} {ETF_CONFIG[k].sublabel}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 摘要卡片 */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:8, marginBottom:16 }}>
        {[
          { label:`${leftKey} 持股數`,  val:`${Object.keys(leftMap).length} 支`,  c:leftCfg.accent },
          { label:`${rightKey} 持股數`, val:`${Object.keys(rightMap).length} 支`, c:rightCfg.accent },
          { label:"共同持股",           val:`${bothCount} 支`,  c:"#6366f1" },
          { label:`${leftKey} 獨有`,    val:`${leftOnly} 支`,   c:leftCfg.accent },
          { label:`${rightKey} 獨有`,   val:`${rightOnly} 支`,  c:rightCfg.accent },
        ].map(({ label, val, c }) => (
          <div key={label} style={{ border:"1px solid #f3f4f6", borderRadius:8, padding:"10px 12px", background:"#fafafa" }}>
            <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:15, fontWeight:600, color:c }}>{val}</div>
          </div>
        ))}
      </div>

      {/* 控制列 */}
      <div className="filter-row" style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap", alignItems:"center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜尋代號或名稱"
          style={{ border:"1px solid #e5e7eb", borderRadius:6, padding:"5px 10px", fontSize:12, width:140, outline:"none" }} />
        <div style={{ display:"flex", gap:4 }}>
          {[["left",`${leftKey}↓`],["right",`${rightKey}↓`],["diff","差距"],["both","共同"]].map(([v,l]) => (
            <button key={v} onClick={() => setSortBy(v)}
              style={{ border:"1px solid #e5e7eb", borderRadius:5, padding:"4px 9px", fontSize:11, cursor:"pointer",
                background: sortBy===v ? "#111827" : "white",
                color:      sortBy===v ? "white"   : "#6b7280" }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize:11, color:"#d1d5db", marginLeft:"auto" }}>{allCodes.length} 支</span>

      </div>

      {/* 並排比較表 */}
      <div style={{ border:"1px solid #f3f4f6", borderRadius:8, overflow:"hidden" }}>
        {/* 表頭 */}
        <div style={{ display:"grid", gridTemplateColumns:"80px 1fr 180px 20px 180px", background:"#fafafa", borderBottom:"1px solid #f3f4f6", padding:"9px 12px", fontSize:11, color:"#9ca3af", fontWeight:500, gap:8, alignItems:"center" }}>
          <div>代號</div>
          <div>名稱</div>
          <div style={{ textAlign:"right", color:leftCfg.accent, fontWeight:600 }}>{leftKey}</div>
          <div />
          <div style={{ textAlign:"left",  color:rightCfg.accent, fontWeight:600 }}>{rightKey}</div>
        </div>

        {allCodes.map(row => {
          const isShared = row.inBoth;
          const leftPct  = (row.left  / maxWeight) * 100;
          const rightPct = (row.right / maxWeight) * 100;
          return (
            <div key={row.code} style={{ display:"grid", gridTemplateColumns:"80px 1fr 180px 20px 180px", padding:"7px 12px", borderBottom:"1px solid #f9fafb", gap:8, alignItems:"center",
              background: isShared ? "#fafffe" : "white" }}>
              {/* 代號 */}
              <div style={{ fontSize:11, color:"#6b7280", fontFamily:"monospace" }}>{row.code}</div>
              {/* 名稱 */}
              <div style={{ fontSize:12, color:"#111827", fontWeight: isShared ? 500 : 400, display:"flex", alignItems:"center", gap:5 }}>
                {row.name}
                {isShared && <span style={{ fontSize:9, background:"#e0e7ff", color:"#6366f1", borderRadius:3, padding:"1px 4px", flexShrink:0 }}>共同</span>}
              </div>
              {/* 左側長條 */}
              <div style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"flex-end" }}>
                <span style={{ fontSize:12, fontWeight:600, color: row.left ? leftCfg.accent : "#e5e7eb", fontFamily:"monospace", minWidth:44, textAlign:"right" }}>
                  {row.left ? `${row.left.toFixed(2)}%` : "—"}
                </span>
                <div style={{ width:80, height:6, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${leftPct}%`, background: row.left ? leftCfg.accent : "transparent", borderRadius:3, float:"right" }} />
                </div>
              </div>
              {/* 中間分隔 */}
              <div style={{ width:1, height:20, background:"#f3f4f6", margin:"0 auto" }} />
              {/* 右側長條 */}
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ width:80, height:6, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${rightPct}%`, background: row.right ? rightCfg.accent : "transparent", borderRadius:3 }} />
                </div>
                <span style={{ fontSize:12, fontWeight:600, color: row.right ? rightCfg.accent : "#e5e7eb", fontFamily:"monospace", minWidth:44 }}>
                  {row.right ? `${row.right.toFixed(2)}%` : "—"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 個股追蹤 ─────────────────────────────────────────
function StockTracker({ latestSnap, loading }) {
  const ETF_KEYS = Object.keys(ETF_CONFIG);
  const [input,     setInput]     = useState("");
  const [watchlist, setWatchlist] = useState(["2330","2454","2308"]);

  // 從 latestSnap 解出 allData / allDates
  const allData  = useMemo(() => Object.fromEntries(ETF_KEYS.map(k => [k, latestSnap[k]?.stocks || []])), [latestSnap]);
  const allDates = useMemo(() => Object.fromEntries(ETF_KEYS.map(k => [k, latestSnap[k]?.date   || ""])), [latestSnap]);

  // 建立全股票索引 code → { name, etfs: { ETF: weight } }
  const stockIndex = useMemo(() => {
    const idx = {};
    ETF_KEYS.forEach(etfKey => {
      (allData[etfKey] || []).forEach(s => {
        if (!idx[s.code]) idx[s.code] = { code: s.code, name: s.name, etfs: {} };
        idx[s.code].etfs[etfKey] = s.weight;
      });
    });
    return idx;
  }, [allData]);

  // 搜尋建議
  const suggestions = useMemo(() => {
    const q = input.trim();
    if (!q) return [];
    return Object.values(stockIndex)
      .filter(s => s.code.startsWith(q) || s.name.includes(q))
      .slice(0, 8);
  }, [input, stockIndex]);

  const addToWatchlist = (code) => {
    if (!watchlist.includes(code)) setWatchlist(prev => [...prev, code]);
    setInput("");
  };

  const removeFromWatchlist = (code) => {
    setWatchlist(prev => prev.filter(c => c !== code));
  };

  const watchData = watchlist
    .map(code => stockIndex[code])
    .filter(Boolean);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200 }}>
      <div style={{ width:24, height:24, border:"2px solid #e5e7eb", borderTop:"2px solid #f59e0b", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
    </div>
  );

  return (
    <div>
      {/* 資料日期列 */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:"4px 12px", marginBottom:10, fontSize:11, color:"#9ca3af" }}>
        <span>資料日期：</span>
        {ETF_KEYS.map(k => allDates[k] ? (
          <span key={k}>
            <span style={{ color:ETF_CONFIG[k].accent, fontWeight:600 }}>{k}</span>
            {" "}{allDates[k]}
          </span>
        ) : null)}
      </div>

      {/* 搜尋輸入 */}
      <div style={{ position:"relative", marginBottom:16 }}>
        <div style={{ display:"flex", gap:8 }}>
          <div style={{ position:"relative", flex:1 }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && suggestions.length > 0) addToWatchlist(suggestions[0].code);
              }}
              placeholder="輸入股票代號或名稱（如 2330 或 台積電）"
              style={{ width:"100%", border:"1px solid #e5e7eb", borderRadius:7, padding:"8px 12px", fontSize:13, outline:"none" }}
            />
            {/* 搜尋建議 */}
            {suggestions.length > 0 && (
              <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"white", border:"1px solid #e5e7eb", borderRadius:7, boxShadow:"0 4px 12px rgba(0,0,0,0.08)", zIndex:10, marginTop:4 }}>
                {suggestions.map(s => (
                  <div key={s.code} onClick={() => addToWatchlist(s.code)}
                    style={{ padding:"8px 12px", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:13 }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                    onMouseLeave={e => e.currentTarget.style.background = "white"}>
                    <span>
                      <span style={{ fontFamily:"monospace", color:"#6b7280", marginRight:8 }}>{s.code}</span>
                      <span style={{ color:"#111827" }}>{s.name}</span>
                    </span>
                    <span style={{ fontSize:11, color:"#9ca3af" }}>
                      {Object.keys(s.etfs).length} 檔持有
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => { if (suggestions.length > 0) addToWatchlist(suggestions[0].code); }}
            style={{ border:"none", background:"#f59e0b", color:"white", borderRadius:7, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
            追蹤
          </button>
        </div>
      </div>


      {watchData.length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#9ca3af", fontSize:13 }}>
          輸入股票代號開始追蹤
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {watchData.map(s => {
            const maxW = Math.max(...ETF_KEYS.map(k => s.etfs[k] || 0), 0.1);
            const etfCount = Object.keys(s.etfs).length;
            return (
              <div key={s.code} style={{ border:"1px solid #f3f4f6", borderRadius:10, padding:"14px 16px", background:"white" }}>
                {/* 股票標頭 */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                  <div>
                    <span style={{ fontFamily:"monospace", fontSize:12, color:"#9ca3af", marginRight:8 }}>{s.code}</span>
                    <span style={{ fontSize:16, fontWeight:600, color:"#111827" }}>{s.name}</span>
                    <span style={{ marginLeft:8, fontSize:11,
                      background: etfCount===ETF_KEYS.length?"#fee2e2":etfCount>=2?"#fef3c7":"#eff6ff",
                      color: etfCount===ETF_KEYS.length?"#dc2626":etfCount>=2?"#d97706":"#2563eb",
                      borderRadius:4, padding:"1px 7px" }}>
                      {etfCount}/{ETF_KEYS.length} 檔持有
                    </span>
                  </div>
                  <button onClick={() => removeFromWatchlist(s.code)}
                    style={{ border:"none", background:"none", color:"#d1d5db", fontSize:16, cursor:"pointer", padding:"0 4px" }}>✕</button>
                </div>

                {/* 各 ETF 持股比重長條 */}
                <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                  {ETF_KEYS.map(k => {
                    const w = s.etfs[k];
                    const cfg = ETF_CONFIG[k];
                    return (
                      <div key={k} style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:56, fontSize:11, fontFamily:"monospace", color: w ? cfg.accent : "#d1d5db", fontWeight: w?600:400, textAlign:"right" }}>
                          {k}
                        </div>
                        <div style={{ flex:1, height:8, background:"#f3f4f6", borderRadius:4, overflow:"hidden" }}>
                          <div style={{ height:"100%", width: w ? `${(w/maxW)*100}%` : "0%", background:cfg.accent, borderRadius:4, transition:"width .4s ease" }} />
                        </div>
                        <div style={{ width:52, fontSize:12, fontFamily:"monospace", fontWeight:700, color: w ? cfg.accent : "#e5e7eb", textAlign:"right" }}>
                          {w ? `${w.toFixed(2)}%` : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* 加總曝險（等比重） */}
                <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid #f9fafb", display:"flex", justifyContent:"flex-end", gap:16, fontSize:12 }}>
                  <span style={{ color:"#9ca3af" }}>等比重加總曝險</span>
                  <span style={{ fontFamily:"monospace", fontWeight:700, color:"#6366f1" }}>
                    {(ETF_KEYS.reduce((sum, k) => sum + (s.etfs[k] || 0) * (1/ETF_KEYS.length), 0)).toFixed(3)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 快速新增常見股票 */}
      <div style={{ marginTop:16, borderTop:"1px solid #f9fafb", paddingTop:14 }}>
        <div style={{ fontSize:11, color:"#9ca3af", marginBottom:8 }}>快速新增</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
          {["2330","2454","2308","3711","2881","2317","6669","2345","3665","8046"]
            .filter(c => !watchlist.includes(c))
            .map(code => {
              const s = stockIndex[code];
              if (!s) return null;
              return (
                <button key={code} onClick={() => addToWatchlist(code)}
                  style={{ border:"1px solid #e5e7eb", borderRadius:5, padding:"4px 10px", fontSize:11, cursor:"pointer", background:"white", color:"#374151" }}>
                  {code} {s.name}
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
}

// ── 趨勢分析 Tab ──────────────────────────────────────
function TrendAnalysis({ allHistory, loading }) {
  const ETF_KEYS = Object.keys(ETF_CONFIG);
  const [activeView, setActiveView] = useState("nav");     // nav | stock
  const [stockCode,  setStockCode]  = useState("2330");
  const [stockInput, setStockInput] = useState("2330");

  // 集中度警示：前5大合計
  const concentrationData = useMemo(() => {
    return ETF_KEYS.map(key => {
      const history = allHistory[key] || [];
      const latest  = history[history.length - 1];
      if (!latest?.stocks?.length) return null;
      const stocks  = latest.stocks;
      const top5    = stocks.slice(0,5).reduce((s,x) => s + x.weight, 0);
      const top10   = stocks.slice(0,10).reduce((s,x) => s + x.weight, 0);
      const top1    = stocks[0]?.weight || 0;
      return { key, date: latest.date, top1, top5, top10, top1name: stocks[0]?.name || "" };
    }).filter(Boolean);
  }, [allHistory]);

  // 淨值走勢資料
  const navData = useMemo(() => {
    return ETF_KEYS.map(key => {
      const history = allHistory[key] || [];
      return {
        key,
        points: history
          .filter(d => d.nav && parseFloat(d.nav) > 0)
          .map(d => ({ date: d.date, nav: parseFloat(d.nav) }))
          .slice(-30),
      };
    });
  }, [allHistory]);

  // 個股歷史權重
  const stockTrendData = useMemo(() => {
    return ETF_KEYS.map(key => {
      const history = allHistory[key] || [];
      return {
        key,
        points: history
          .map(d => {
            const s = (d.stocks || []).find(x => x.code === stockCode);
            return s ? { date: d.date, weight: s.weight } : null;
          })
          .filter(Boolean)
          .slice(-30),
      };
    }).filter(d => d.points.length > 0);
  }, [allHistory, stockCode]);

  // SVG 折線圖元件
  const LineChart = ({ series, yLabel, colorMap, formatY }) => {
    const W = 100, H = 60;
    const allVals = series.flatMap(s => s.points.map(p => p.y));
    if (!allVals.length) return <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:12 }}>資料不足（需要至少 2 個交易日）</div>;

    const minY = Math.min(...allVals), maxY = Math.max(...allVals);
    const rangeY = maxY - minY || 1;

    const toX = (i, len) => len < 2 ? W/2 : (i / (len-1)) * W;
    const toY = (v) => H - ((v - minY) / rangeY) * H * 0.85 - H * 0.075;

    const dates = series[0]?.points.map(p => p.x) || [];

    return (
      <div>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:180, display:"block" }}>
          {/* Grid */}
          {[0,1,2,3].map(i => (
            <line key={i} x1="0" y1={H*0.075 + i*(H*0.85/3)} x2={W} y2={H*0.075 + i*(H*0.85/3)}
              stroke="#f3f4f6" strokeWidth="0.5" />
          ))}
          {/* Lines */}
          {series.map(({ key, points }) => {
            if (points.length < 2) return null;
            const d = points.map((p,i) => `${i===0?"M":"L"}${toX(i,points.length)},${toY(p.y)}`).join(" ");
            return (
              <g key={key}>
                <path d={d} fill="none" stroke={colorMap[key]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                {points.map((p,i) => (
                  <circle key={i} cx={toX(i,points.length)} cy={toY(p.y)} r="1" fill={colorMap[key]} />
                ))}
              </g>
            );
          })}
        </svg>
        {/* X軸日期 */}
        <div style={{ display:"flex", justifyContent:"space-between", marginTop:4, fontSize:10, color:"#9ca3af" }}>
          {dates.length > 0 && <>
            <span>{dates[0]?.slice(5)}</span>
            {dates.length > 4 && <span>{dates[Math.floor(dates.length/2)]?.slice(5)}</span>}
            <span>{dates[dates.length-1]?.slice(5)}</span>
          </>}
        </div>
        {/* 圖例 */}
        <div style={{ display:"flex", gap:16, marginTop:10, flexWrap:"wrap" }}>
          {series.map(({ key, points }) => (
            <div key={key} style={{ display:"flex", alignItems:"center", gap:5 }}>
              <div style={{ width:12, height:3, background:colorMap[key], borderRadius:2 }} />
              <span style={{ fontSize:11, color:"#6b7280" }}>
                {key}
                {points.length > 0 && <span style={{ fontFamily:"monospace", marginLeft:4, color:colorMap[key], fontWeight:600 }}>
                  {formatY(points[points.length-1].y)}
                </span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const colorMap = Object.fromEntries(ETF_KEYS.map(k => [k, ETF_CONFIG[k].accent]));

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200 }}>
      <div style={{ width:24, height:24, border:"2px solid #e5e7eb", borderTop:"2px solid #14b8a6", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
    </div>
  );

  return (
    <div>
      {/* 集中度警示 */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:12, fontWeight:600, color:"#111827", marginBottom:10 }}>集中度警示</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:8 }}>
          {concentrationData.map(d => {
            const top5Warn  = d.top5  > 50;
            const top1Warn  = d.top1  > 25;
            const warn      = top5Warn || top1Warn;
            const cfg       = ETF_CONFIG[d.key];
            return (
              <div key={d.key} style={{ border:`1px solid ${warn ? "#fde68a" : "#f3f4f6"}`, borderRadius:9,
                padding:"12px 14px", background: warn ? "#fffbeb" : "#fafafa" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:cfg.accent }}>{d.key}</span>
                  {warn && <span style={{ fontSize:10, background:"#fef3c7", color:"#d97706", borderRadius:4, padding:"1px 7px", fontWeight:600 }}>⚠ 集中</span>}
                </div>
                {[
                  { label:`最大持股（${d.top1name}）`, val:d.top1, warn: d.top1 > 25 },
                  { label:"前5大合計",                 val:d.top5, warn: d.top5 > 50 },
                  { label:"前10大合計",                val:d.top10, warn: d.top10 > 70 },
                ].map(({ label, val, warn:w }) => (
                  <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12, marginBottom:5 }}>
                    <span style={{ color:"#6b7280" }}>{label}</span>
                    <span style={{ fontFamily:"monospace", fontWeight:700, color: w ? "#d97706" : "#111827" }}>{val.toFixed(2)}%</span>
                  </div>
                ))}
                {/* 前5大長條 */}
                <div style={{ marginTop:8, height:4, background:"#f3f4f6", borderRadius:2, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${Math.min(d.top5, 100)}%`, background: d.top5>50?"#f59e0b":cfg.accent, borderRadius:2 }} />
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize:10, color:"#9ca3af", marginTop:6 }}>
          警示門檻：最大單一持股 &gt; 25%，前5大合計 &gt; 50%，前10大合計 &gt; 70%
        </div>
      </div>

      {/* 切換：淨值 / 個股 */}
      <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center", flexWrap:"wrap" }}>
        {[["nav","ETF 淨值走勢"],["stock","個股歷史權重"]].map(([v,l]) => (
          <button key={v} onClick={() => setActiveView(v)}
            style={{ border:"1px solid #e5e7eb", borderRadius:6, padding:"6px 14px", fontSize:12, cursor:"pointer",
              background: activeView===v ? "#14b8a6" : "white",
              color:      activeView===v ? "white"   : "#6b7280",
              fontWeight: activeView===v ? 600 : 400 }}>{l}</button>
        ))}
        {activeView === "stock" && (
          <div style={{ display:"flex", gap:6, marginLeft:"auto" }}>
            <input value={stockInput} onChange={e => setStockInput(e.target.value)}
              onKeyDown={e => { if (e.key==="Enter") setStockCode(stockInput.trim()); }}
              placeholder="股票代號"
              style={{ border:"1px solid #e5e7eb", borderRadius:6, padding:"5px 10px", fontSize:12, width:100, outline:"none" }} />
            <button onClick={() => setStockCode(stockInput.trim())}
              style={{ border:"none", background:"#14b8a6", color:"white", borderRadius:6, padding:"5px 12px", fontSize:12, cursor:"pointer" }}>
              查詢
            </button>
          </div>
        )}
      </div>

      {/* 圖表 */}
      <div style={{ border:"1px solid #f3f4f6", borderRadius:10, padding:"20px" }}>
        {activeView === "nav" ? (
          <>
            <div style={{ fontSize:13, fontWeight:600, color:"#111827", marginBottom:16 }}>
              ETF 淨值走勢（近30交易日）
            </div>
            <LineChart
              series={navData.filter(d => d.points.length > 0).map(d => ({
                key: d.key,
                points: d.points.map(p => ({ x: p.date, y: p.nav }))
              }))}
              colorMap={colorMap}
              formatY={v => `$${v.toFixed(2)}`}
            />
          </>
        ) : (
          <>
            <div style={{ fontSize:13, fontWeight:600, color:"#111827", marginBottom:4 }}>
              {stockCode} 持股比重走勢（近30交易日）
            </div>
            {stockTrendData.length === 0
              ? <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:12 }}>
                  找不到 {stockCode} 的歷史資料
                </div>
              : <LineChart
                  series={stockTrendData.map(d => ({
                    key: d.key,
                    points: d.points.map(p => ({ x: p.date, y: p.weight }))
                  }))}
                  colorMap={colorMap}
                  formatY={v => `${v.toFixed(2)}%`}
                />
            }
            {/* 快速代號 */}
            <div style={{ marginTop:12, display:"flex", gap:6, flexWrap:"wrap" }}>
              {["2330","2454","2308","3711","2381","2317"].map(c => (
                <button key={c} onClick={() => { setStockCode(c); setStockInput(c); }}
                  style={{ border:`1px solid ${stockCode===c?"#14b8a6":"#e5e7eb"}`, borderRadius:5, padding:"3px 9px",
                    fontSize:11, cursor:"pointer", background: stockCode===c?"#f0fdfa":"white",
                    color: stockCode===c?"#14b8a6":"#6b7280" }}>{c}</button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── 產業分類對照表（台股主要成分股）─────────────────────
const INDUSTRY_MAP = {
  // 半導體
  "2330":"半導體", "2303":"半導體", "2308":"半導體", "2454":"半導體",
  "3711":"半導體", "2379":"半導體", "3034":"半導體", "2337":"半導體",
  "2344":"半導體", "2408":"半導體", "3443":"半導體", "6770":"半導體",
  "3008":"半導體", "2049":"半導體", "6533":"半導體", "3529":"半導體",
  "3714":"半導體", "2449":"半導體", "2388":"半導體", "3016":"半導體",
  "4966":"半導體", "6415":"半導體", "3413":"半導體", "2368":"半導體",
  "2351":"半導體",
  // 電子零組件
  "2317":"電子零組件", "2327":"電子零組件", "2354":"電子零組件",
  "2382":"電子零組件", "2356":"電子零組件", "3231":"電子零組件",
  "2395":"電子零組件", "2353":"電子零組件", "2357":"電子零組件",
  "3045":"電子零組件", "2377":"電子零組件", "2301":"電子零組件",
  "6669":"電子零組件",
  // 電腦及周邊
  "2376":"電腦及周邊", "2393":"電腦及周邊", "3105":"電腦及周邊",
  // 光電
  "2498":"光電", "3481":"光電", "2475":"光電", "3019":"光電",
  "2340":"光電",
  // 通訊網路
  "2412":"通訊網路", "4904":"通訊網路",
  "2302":"通訊網路",
  // 電機機械
  "2352":"電機機械", "1590":"電機機械",
  // 電力設備
  // （待補正確代號）
  // 電子通路
  "2347":"電子通路", "2360":"電子通路",
  // 其他電子
  "2399":"其他電子", "6488":"其他電子", "3450":"其他電子",
  "2383":"其他電子",
  // 金融保險
  "2881":"金融保險", "2882":"金融保險", "2883":"金融保險",
  "2884":"金融保險", "2885":"金融保險", "2886":"金融保險",
  "2887":"金融保險", "2888":"金融保險", "2890":"金融保險",
  "2891":"金融保險", "2892":"金融保險", "5880":"金融保險",
  "2801":"金融保險", "2809":"金融保險",
  // 食品
  "1216":"食品", "1101":"食品", "1103":"食品",
  // 塑化
  "1301":"塑化", "1303":"塑化", "1326":"塑化", "6505":"塑化",
  // 鋼鐵
  "2002":"鋼鐵", "2006":"鋼鐵",
  // 水泥
  "1102":"水泥", "1104":"水泥",
  // 航運
  "2603":"航運", "2609":"航運", "2615":"航運", "2610":"航運",
  "2618":"航運",
  // 汽車
  "2207":"汽車",
  // 觀光餐飲
  "2727":"觀光餐飲",
  // 電信
  "2412":"電信", "3045":"電信", "4904":"電信",
  // 生技醫療
  "4711":"生技醫療", "4763":"生技醫療", "1786":"生技醫療",
  "6548":"生技醫療",
  // 貿易百貨
  "2912":"貿易百貨", "2903":"貿易百貨",
  // 建材營造
  "2504":"建材營造", "2511":"建材營造",
  // 油電燃氣
  "6505":"油電燃氣",
  // 紡織纖維
  "1402":"紡織纖維",
  // 電子商務/軟體
  "5243":"軟體服務", "3697":"軟體服務",
};

// 產業顏色對照
const INDUSTRY_COLOR = {
  "半導體":    "#2563eb",
  "電子零組件":"#7c3aed",
  "電腦及周邊":"#0891b2",
  "光電":      "#0284c7",
  "通訊網路":  "#6366f1",
  "電機機械":  "#8b5cf6",
  "其他電子":  "#a78bfa",
  "金融保險":  "#059669",
  "食品":      "#d97706",
  "塑化":      "#b45309",
  "鋼鐵":      "#78716c",
  "水泥":      "#a8a29e",
  "航運":      "#0369a1",
  "汽車":      "#dc2626",
  "電信":      "#16a34a",
  "生技醫療":  "#ec4899",
  "貿易百貨":  "#f59e0b",
  "建材營造":  "#84cc16",
  "油電燃氣":  "#f97316",
  "紡織纖維":  "#06b6d4",
  "軟體服務":  "#10b981",
  "其他":      "#9ca3af",
};

const getIndustry = (code) => INDUSTRY_MAP[code] || "其他";

// ── 產業分類分析 ─────────────────────────────────────
function IndustryAnalysis({ latestSnap, loading }) {
  const ETF_KEYS = Object.keys(ETF_CONFIG);
  const [viewMode, setViewMode] = useState("bar");   // bar | heatmap | table
  const [selectedEtf, setSelectedEtf] = useState("all");
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  // 從 latestSnap 解出 allData
  const allData = useMemo(() => Object.fromEntries(ETF_KEYS.map(k => [k, latestSnap[k]?.stocks || []])), [latestSnap]);

  // 計算每檔 ETF 的產業分布
  const etfIndustry = useMemo(() => {
    const result = {};
    ETF_KEYS.forEach(k => {
      const stocks = allData[k] || [];
      const map = {};
      let unclassified = 0;
      stocks.forEach(s => {
        const ind = getIndustry(s.code);
        if (!map[ind]) map[ind] = { weight: 0, stocks: [] };
        map[ind].weight += s.weight;
        map[ind].stocks.push(s);
      });
      result[k] = map;
    });
    return result;
  }, [allData]);

  // 合併所有 ETF 的產業清單
  const allIndustries = useMemo(() => {
    const set = new Set();
    Object.values(etfIndustry).forEach(m => Object.keys(m).forEach(i => set.add(i)));
    return [...set].sort((a, b) => {
      // 以所有 ETF 加總比重排序
      const wa = ETF_KEYS.reduce((s,k) => s + (etfIndustry[k]?.[a]?.weight||0), 0);
      const wb = ETF_KEYS.reduce((s,k) => s + (etfIndustry[k]?.[b]?.weight||0), 0);
      return wb - wa;
    });
  }, [etfIndustry]);

  // 選定 ETF 的產業資料（或等比重合併）
  const displayData = useMemo(() => {
    if (selectedEtf === "all") {
      // 等比重平均
      const merged = {};
      ETF_KEYS.forEach(k => {
        Object.entries(etfIndustry[k] || {}).forEach(([ind, d]) => {
          if (!merged[ind]) merged[ind] = { weight: 0, stocks: [] };
          merged[ind].weight += d.weight * (1/ETF_KEYS.length);
          d.stocks.forEach(s => {
            const exist = merged[ind].stocks.find(x => x.code === s.code);
            if (!exist) merged[ind].stocks.push({ ...s, etfs: [k] });
            else exist.etfs = [...(exist.etfs||[k]), k];
          });
        });
      });
      return merged;
    }
    return etfIndustry[selectedEtf] || {};
  }, [etfIndustry, selectedEtf]);

  const maxWeight = Math.max(...Object.values(displayData).map(d => d.weight), 0.1);

  // 選中產業的成分股
  const selStocks = useMemo(() => {
    if (!selectedIndustry || !displayData[selectedIndustry]) return [];
    return [...displayData[selectedIndustry].stocks].sort((a,b) => b.weight - a.weight);
  }, [displayData, selectedIndustry]);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200 }}>
      <div style={{ width:24, height:24, border:"2px solid #e5e7eb", borderTop:"2px solid #10b981", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
    </div>
  );

  const accent = selectedEtf === "all" ? "#10b981" : ETF_CONFIG[selectedEtf]?.accent;

  return (
    <div>
      {/* 控制列 */}
      <div className="filter-row" style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        {/* ETF 選擇 */}
        <div style={{ display:"flex", gap:4 }}>
          {[["all","等比重合併"], ...ETF_KEYS.map(k => [k, k])].map(([v, l]) => {
            const ac = v === "all" ? "#10b981" : ETF_CONFIG[v]?.accent;
            return (
              <button key={v} onClick={() => { setSelectedEtf(v); setSelectedIndustry(null); }}
                style={{ border:`1px solid ${selectedEtf===v ? ac : "#e5e7eb"}`,
                  borderRadius:5, padding:"4px 10px", fontSize:11, cursor:"pointer",
                  background: selectedEtf===v ? ac : "white",
                  color: selectedEtf===v ? "white" : "#6b7280", transition:"all .15s" }}>{l}</button>
            );
          })}
        </div>

        {/* 視圖切換 */}
        <div style={{ display:"flex", gap:4, marginLeft:"auto" }}>
          {[["bar","長條圖"],["heatmap","熱力圖"],["table","明細表"]].map(([v,l]) => (
            <button key={v} onClick={() => setViewMode(v)}
              style={{ border:"1px solid #e5e7eb", borderRadius:5, padding:"4px 10px", fontSize:11, cursor:"pointer",
                background: viewMode===v ? "#f3f4f6" : "white",
                color: viewMode===v ? "#111827" : "#6b7280" }}>{l}</button>
          ))}
        </div>
      </div>

      {/* 摘要卡 */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:8, marginBottom:16 }}>
        {(() => {
          const topInd = Object.entries(displayData).sort((a,b)=>b[1].weight-a[1].weight)[0];
          const totalStocks = new Set(Object.values(displayData).flatMap(d=>d.stocks.map(s=>s.code))).size;
          const classified = Object.entries(displayData).filter(([k])=>k!=="其他").reduce((s,[,d])=>s+d.weight,0);
          return [
            { label:"最大產業", val: topInd ? `${topInd[0]}` : "—", sub: topInd ? `${topInd[1].weight.toFixed(1)}%` : "", c: topInd ? (INDUSTRY_COLOR[topInd[0]]||"#6b7280") : "#6b7280" },
            { label:"產業數",   val: `${Object.keys(displayData).length} 類`, c:"#6b7280" },
            { label:"成分股數", val: `${totalStocks} 支`, c:"#6b7280" },
            { label:"已分類比重", val: `${classified.toFixed(1)}%`, c:"#10b981" },
          ];
        })().map(({ label, val, sub, c }) => (
          <div key={label} style={{ border:"1px solid #f3f4f6", borderRadius:8, padding:"10px 12px", background:"#fafafa" }}>
            <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:15, fontWeight:600, color:c }}>{val}</div>
            {sub && <div style={{ fontSize:11, color:"#9ca3af" }}>{sub}</div>}
          </div>
        ))}
      </div>

      {/* ── 長條圖視圖 ── */}
      {viewMode === "bar" && (
        <div style={{ border:"1px solid #f3f4f6", borderRadius:8, overflow:"hidden" }}>
          {Object.entries(displayData)
            .sort((a,b) => b[1].weight - a[1].weight)
            .map(([ind, d]) => {
              const color = INDUSTRY_COLOR[ind] || "#9ca3af";
              const isSelected = selectedIndustry === ind;
              const pct = (d.weight / maxWeight) * 100;
              return (
                <div key={ind}
                  onClick={() => setSelectedIndustry(isSelected ? null : ind)}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", cursor:"pointer",
                    background: isSelected ? `${color}10` : "white", borderBottom:"1px solid #f9fafb",
                    transition:"background .1s" }}
                  onMouseEnter={e=>{ if(!isSelected) e.currentTarget.style.background="#f9fafb"; }}
                  onMouseLeave={e=>{ if(!isSelected) e.currentTarget.style.background="white"; }}>
                  {/* 產業標籤 */}
                  <div className="industry-label" style={{ minWidth:80, fontSize:12, fontWeight: isSelected?600:400, color: isSelected?color:"#374151" }}>{ind}</div>
                  {/* 長條 */}
                  <div style={{ flex:1, height:18, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:3, transition:"width .4s", opacity:0.85 }} />
                  </div>
                  {/* 比重數值 */}
                  <div style={{ minWidth:48, textAlign:"right", fontSize:13, fontWeight:600, color, fontFamily:"monospace" }}>
                    {d.weight.toFixed(1)}%
                  </div>
                  {/* 股票數 */}
                  <div style={{ minWidth:36, textAlign:"right", fontSize:11, color:"#9ca3af" }}>
                    {d.stocks.length}支
                  </div>
                  <div style={{ fontSize:10, color:"#d1d5db" }}>{isSelected?"▲":"▼"}</div>
                </div>
              );
          })}
        </div>
      )}

      {/* ── 熱力圖視圖（各 ETF x 各產業）── */}
      {viewMode === "heatmap" && (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#fafafa" }}>
                <th style={{ padding:"8px 12px", textAlign:"left", fontSize:11, color:"#9ca3af", fontWeight:500, borderBottom:"1px solid #f3f4f6", minWidth:80 }}>產業</th>
                {ETF_KEYS.map(k => (
                  <th key={k} style={{ padding:"8px 12px", textAlign:"center", fontSize:11, color:ETF_CONFIG[k].accent, fontWeight:600, borderBottom:"1px solid #f3f4f6", minWidth:80 }}>{k}</th>
                ))}
                <th style={{ padding:"8px 12px", textAlign:"right", fontSize:11, color:"#9ca3af", fontWeight:500, borderBottom:"1px solid #f3f4f6" }}>合計</th>
              </tr>
            </thead>
            <tbody>
              {allIndustries.map(ind => {
                const color = INDUSTRY_COLOR[ind] || "#9ca3af";
                const total = ETF_KEYS.reduce((s,k) => s + (etfIndustry[k]?.[ind]?.weight||0), 0);
                const maxCell = Math.max(...ETF_KEYS.map(k => etfIndustry[k]?.[ind]?.weight||0));
                return (
                  <tr key={ind} style={{ borderBottom:"1px solid #f9fafb" }}
                    onClick={() => setSelectedIndustry(selectedIndustry===ind ? null : ind)}>
                    <td style={{ padding:"8px 12px", fontWeight:500, color:"#374151", cursor:"pointer" }}>
                      <span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", background:color, marginRight:6 }} />
                      {ind}
                    </td>
                    {ETF_KEYS.map(k => {
                      const w = etfIndustry[k]?.[ind]?.weight || 0;
                      const intensity = maxCell > 0 ? w / maxCell : 0;
                      return (
                        <td key={k} style={{ padding:"8px 12px", textAlign:"center", fontFamily:"monospace",
                          background: w > 0 ? `${color}${Math.round(intensity * 40 + 8).toString(16).padStart(2,"0")}` : "white",
                          color: w > 0 ? color : "#e5e7eb", fontWeight: w > 0 ? 600 : 400 }}>
                          {w > 0 ? `${w.toFixed(1)}%` : "—"}
                        </td>
                      );
                    })}
                    <td style={{ padding:"8px 12px", textAlign:"right", fontFamily:"monospace", fontWeight:700, color }}>
                      {total.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── 明細表視圖 ── */}
      {viewMode === "table" && (
        <div>
          {Object.entries(displayData)
            .sort((a,b) => b[1].weight - a[1].weight)
            .map(([ind, d]) => {
              const color = INDUSTRY_COLOR[ind] || "#9ca3af";
              const isOpen = selectedIndustry === ind;
              return (
                <div key={ind} style={{ border:"1px solid #f3f4f6", borderRadius:8, marginBottom:6, overflow:"hidden" }}>
                  <div onClick={() => setSelectedIndustry(isOpen ? null : ind)}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", cursor:"pointer",
                      background: isOpen ? `${color}10` : "white" }}>
                    <span style={{ width:10, height:10, borderRadius:2, background:color, flexShrink:0 }} />
                    <span style={{ flex:1, fontSize:13, fontWeight:600, color:"#111827" }}>{ind}</span>
                    <span style={{ fontSize:12, color, fontWeight:700, fontFamily:"monospace" }}>{d.weight.toFixed(1)}%</span>
                    <span style={{ fontSize:11, color:"#9ca3af" }}>{d.stocks.length}支</span>
                    <span style={{ fontSize:10, color:"#d1d5db" }}>{isOpen?"▲":"▼"}</span>
                  </div>
                  {isOpen && (
                    <div style={{ borderTop:"1px solid #f3f4f6" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                        <thead>
                          <tr style={{ background:"#fafafa" }}>
                            {["代號","名稱","比重", ...ETF_KEYS].map((h,i) => (
                              <th key={h} style={{ padding:"6px 12px", textAlign:i>=2?"right":"left", fontSize:10, color:"#9ca3af", fontWeight:500 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {d.stocks.sort((a,b)=>b.weight-a.weight).map(s => (
                            <tr key={s.code} style={{ borderTop:"1px solid #f9fafb" }}>
                              <td style={{ padding:"7px 12px", color:"#6b7280", fontFamily:"monospace", fontSize:11 }}>{s.code}</td>
                              <td style={{ padding:"7px 12px", color:"#111827" }}>{s.name}</td>
                              <td style={{ padding:"7px 12px", textAlign:"right", fontWeight:600, color, fontFamily:"monospace" }}>{s.weight.toFixed(2)}%</td>
                              {ETF_KEYS.map(k => {
                                const w = (allData[k]||[]).find(x=>x.code===s.code)?.weight;
                                return (
                                  <td key={k} style={{ padding:"7px 12px", textAlign:"right", fontSize:11, fontFamily:"monospace",
                                    color: w ? ETF_CONFIG[k].accent : "#e5e7eb" }}>
                                    {w ? `${w.toFixed(2)}%` : "—"}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
          })}
        </div>
      )}

      {/* 展開：點擊產業顯示成分股（長條圖/熱力圖模式用） */}
      {selectedIndustry && viewMode !== "table" && selStocks.length > 0 && (
        <div style={{ marginTop:12, border:`1px solid ${(INDUSTRY_COLOR[selectedIndustry]||"#9ca3af")}30`,
          borderRadius:8, padding:"12px 14px", background:`${(INDUSTRY_COLOR[selectedIndustry]||"#9ca3af")}05` }}>
          <div style={{ fontSize:12, fontWeight:600, color:INDUSTRY_COLOR[selectedIndustry]||"#6b7280", marginBottom:10 }}>
            {selectedIndustry} — 成分股明細
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {selStocks.map(s => {
              const color = INDUSTRY_COLOR[selectedIndustry] || "#9ca3af";
              return (
                <div key={s.code} style={{ border:`1px solid ${color}25`, borderRadius:6, padding:"5px 10px",
                  background:"white", minWidth:100 }}>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"monospace" }}>{s.code}</div>
                  <div style={{ fontSize:12, fontWeight:500, color:"#111827" }}>{s.name}</div>
                  <div style={{ fontSize:13, fontWeight:700, color, fontFamily:"monospace" }}>{s.weight.toFixed(2)}%</div>
                  {selectedEtf === "all" && s.etfs && (
                    <div style={{ display:"flex", gap:2, marginTop:3, flexWrap:"wrap" }}>
                      {s.etfs.map(k => (
                        <span key={k} style={{ fontSize:8, background:`${ETF_CONFIG[k].accent}18`, color:ETF_CONFIG[k].accent, borderRadius:2, padding:"1px 4px" }}>{k}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── 成分股進出歷史 ────────────────────────────────────
function StockHistory({ allHistory, loading }) {
  const ETF_KEYS = Object.keys(ETF_CONFIG);
  const [search, setSearch] = useState("");
  const [filterEtf, setFilterEtf] = useState("all");
  const [filterType, setFilterType] = useState("all"); // all | in | out
  const [expanded, setExpanded] = useState(null);

  // 從歷史資料中分析每支股票的進出事件
  const events = useMemo(() => {
    if (Object.keys(allHistory).length === 0) return [];

    const evtMap = {}; // code -> { name, events: [{etf, type, date, weight}] }

    ETF_KEYS.forEach(etfKey => {
      const days = [...(allHistory[etfKey] || [])].sort((a, b) => a.date.localeCompare(b.date));
      if (days.length < 2) return;

      days.forEach((day, idx) => {
        if (idx === 0) return;
        const prev = days[idx - 1];
        const prevCodes = new Set((prev.stocks || []).map(s => s.code));
        const currCodes = new Set((day.stocks || []).map(s => s.code));
        const prevMap = Object.fromEntries((prev.stocks || []).map(s => [s.code, s]));
        const currMap = Object.fromEntries((day.stocks || []).map(s => [s.code, s]));

        // 新進入
        currCodes.forEach(code => {
          if (!prevCodes.has(code)) {
            if (!evtMap[code]) evtMap[code] = { name: currMap[code].name, code, etfEvents: {} };
            if (!evtMap[code].etfEvents[etfKey]) evtMap[code].etfEvents[etfKey] = [];
            evtMap[code].etfEvents[etfKey].push({ type: "in", date: day.date, weight: currMap[code].weight });
          }
        });

        // 離開
        prevCodes.forEach(code => {
          if (!currCodes.has(code)) {
            if (!evtMap[code]) evtMap[code] = { name: prevMap[code].name, code, etfEvents: {} };
            if (!evtMap[code].etfEvents[etfKey]) evtMap[code].etfEvents[etfKey] = [];
            evtMap[code].etfEvents[etfKey].push({ type: "out", date: day.date, weight: prevMap[code].weight });
          }
        });
      });
    });

    return Object.values(evtMap).map(item => {
      const allEvts = Object.entries(item.etfEvents).flatMap(([etf, evts]) =>
        evts.map(e => ({ ...e, etf }))
      ).sort((a, b) => b.date.localeCompare(a.date));
      const lastDate = allEvts[0]?.date || "";
      const hasIn  = allEvts.some(e => e.type === "in");
      const hasOut = allEvts.some(e => e.type === "out");
      return { ...item, allEvts, lastDate, hasIn, hasOut };
    }).sort((a, b) => b.lastDate.localeCompare(a.lastDate));
  }, [allHistory]);

  const filtered = useMemo(() => {
    const q = search.trim();
    return events.filter(item => {
      if (q && !item.name.includes(q) && !item.code.includes(q)) return false;
      if (filterEtf !== "all" && !item.etfEvents[filterEtf]) return false;
      if (filterType === "in"  && !item.hasIn)  return false;
      if (filterType === "out" && !item.hasOut) return false;
      return true;
    });
  }, [events, search, filterEtf, filterType]);

  // 各 ETF 目前持有的股票集合（最新一天）
  const currentHoldings = useMemo(() => {
    const map = {};
    ETF_KEYS.forEach(k => {
      const days = allHistory[k] || [];
      const latest = days[days.length - 1];
      map[k] = new Set((latest?.stocks || []).map(s => s.code));
    });
    return map;
  }, [allHistory]);

  const totalIn  = events.reduce((n, e) => n + (e.hasIn  ? 1 : 0), 0);
  const totalOut = events.reduce((n, e) => n + (e.hasOut ? 1 : 0), 0);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200 }}>
      <div style={{ width:24, height:24, border:"2px solid #e5e7eb", borderTop:"2px solid #8b5cf6", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
    </div>
  );

  return (
    <div>
      {/* 摘要卡 */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:8, marginBottom:16 }}>
        {[
          { label:"有變動股票", val:`${events.length} 支`, c:"#8b5cf6" },
          { label:"曾進入",     val:`${totalIn} 支`,  c:"#16a34a" },
          { label:"曾離開",     val:`${totalOut} 支`, c:"#dc2626" },
          { label:"資料天數",   val:`${Math.max(...ETF_KEYS.map(k => (allHistory[k]||[]).length))} 日`, c:"#6b7280" },
        ].map(({ label, val, c }) => (
          <div key={label} style={{ border:"1px solid #f3f4f6", borderRadius:8, padding:"10px 12px", background:"#fafafa" }}>
            <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:15, fontWeight:600, color:c }}>{val}</div>
          </div>
        ))}
      </div>

      {/* 篩選列 */}
      <div className="filter-row" style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜尋代號或名稱"
          style={{ border:"1px solid #e5e7eb", borderRadius:6, padding:"5px 10px", fontSize:12, width:140, outline:"none" }} />

        <select value={filterEtf} onChange={e => setFilterEtf(e.target.value)}
          style={{ border:"1px solid #e5e7eb", borderRadius:6, padding:"5px 8px", fontSize:11, color:"#6b7280", outline:"none" }}>
          <option value="all">全部 ETF</option>
          {ETF_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
        </select>

        <div style={{ display:"flex", gap:4 }}>
          {[["all","全部"],["in","進入"],["out","離開"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilterType(v)}
              style={{ border:"1px solid #e5e7eb", borderRadius:5, padding:"4px 10px", fontSize:11, cursor:"pointer",
                background: filterType===v ? "#8b5cf6" : "white", color: filterType===v ? "white" : "#6b7280" }}>{l}</button>
          ))}
        </div>

        <span style={{ fontSize:11, color:"#d1d5db", marginLeft:"auto" }}>{filtered.length} 筆</span>
      </div>

      {/* 事件列表 */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:48, color:"#9ca3af", fontSize:13 }}>
          {events.length === 0 ? "資料不足（需至少 2 個交易日才能偵測進出）" : "找不到符合條件的結果"}
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {filtered.map(item => {
            const isExp = expanded === item.code;
            const inETFs  = Object.entries(item.etfEvents).filter(([,evts]) => evts.some(e=>e.type==="in")).map(([k])=>k);
            const outETFs = Object.entries(item.etfEvents).filter(([,evts]) => evts.some(e=>e.type==="out")).map(([k])=>k);
            const nowIn   = ETF_KEYS.filter(k => currentHoldings[k]?.has(item.code));

            return (
              <div key={item.code}
                style={{ border:"1px solid #f3f4f6", borderRadius:8, overflow:"hidden",
                  boxShadow: isExp ? "0 1px 6px rgba(0,0,0,0.06)" : "none" }}>
                {/* 卡頭 */}
                <div onClick={() => setExpanded(isExp ? null : item.code)}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", cursor:"pointer", background: isExp ? "#faf5ff" : "white" }}
                  onMouseEnter={e => { if (!isExp) e.currentTarget.style.background="#fafafa"; }}
                  onMouseLeave={e => { if (!isExp) e.currentTarget.style.background="white"; }}>

                  {/* 代號 + 名稱 */}
                  <div style={{ minWidth:0, flex:1 }}>
                    <span style={{ fontSize:11, color:"#9ca3af", fontFamily:"monospace", marginRight:6 }}>{item.code}</span>
                    <span style={{ fontSize:14, fontWeight:600, color:"#111827" }}>{item.name}</span>
                  </div>

                  {/* 目前持有 badges */}
                  <div style={{ display:"flex", gap:3, flexShrink:0 }}>
                    {nowIn.length > 0 && nowIn.map(k => (
                      <span key={k} style={{ fontSize:9, background:`${ETF_CONFIG[k].accent}18`, color:ETF_CONFIG[k].accent,
                        border:`1px solid ${ETF_CONFIG[k].accent}40`, borderRadius:3, padding:"1px 5px", fontWeight:600 }}>{k}</span>
                    ))}
                    {nowIn.length === 0 && (
                      <span style={{ fontSize:9, background:"#fef2f2", color:"#dc2626", border:"1px solid #fecaca", borderRadius:3, padding:"1px 5px" }}>目前未持有</span>
                    )}
                  </div>

                  {/* 進出 badges */}
                  <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                    {inETFs.length  > 0 && <span style={{ fontSize:10, background:"#dcfce7", color:"#16a34a", borderRadius:4, padding:"2px 7px" }}>▲ 進入 {inETFs.length}</span>}
                    {outETFs.length > 0 && <span style={{ fontSize:10, background:"#fef2f2", color:"#dc2626", borderRadius:4, padding:"2px 7px" }}>▼ 離開 {outETFs.length}</span>}
                  </div>

                  {/* 最新事件日期 */}
                  <span style={{ fontSize:11, color:"#9ca3af", flexShrink:0 }}>{item.lastDate}</span>
                  <span style={{ fontSize:10, color:"#d1d5db" }}>{isExp ? "▲" : "▼"}</span>
                </div>

                {/* 展開：時間軸 */}
                {isExp && (
                  <div style={{ padding:"0 14px 14px", borderTop:"1px solid #f3f4f6" }}>
                    {ETF_KEYS.filter(k => item.etfEvents[k]).map(etfKey => {
                      const cfg = ETF_CONFIG[etfKey];
                      const evts = [...item.etfEvents[etfKey]].sort((a,b) => b.date.localeCompare(a.date));
                      return (
                        <div key={etfKey} style={{ marginTop:12 }}>
                          <div style={{ fontSize:11, fontWeight:600, color:cfg.accent, marginBottom:6 }}>{etfKey}</div>
                          <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                            {evts.map((evt, i) => (
                              <div key={i} style={{ display:"flex", alignItems:"center", gap:0 }}>
                                {/* 時間軸線 */}
                                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", width:20, flexShrink:0 }}>
                                  <div style={{ width:8, height:8, borderRadius:"50%",
                                    background: evt.type==="in" ? "#16a34a" : "#dc2626",
                                    border:"2px solid white", boxShadow:`0 0 0 1px ${evt.type==="in"?"#16a34a":"#dc2626"}`,
                                    zIndex:1 }} />
                                  {i < evts.length-1 && <div style={{ width:1, flex:1, minHeight:16, background:"#e5e7eb" }} />}
                                </div>
                                {/* 事件內容 */}
                                <div style={{ display:"flex", alignItems:"center", gap:8, padding:"4px 0 4px 8px", flex:1 }}>
                                  <span style={{ fontSize:10, fontWeight:600, minWidth:28,
                                    color: evt.type==="in" ? "#16a34a" : "#dc2626" }}>
                                    {evt.type==="in" ? "▲ 進" : "▼ 出"}
                                  </span>
                                  <span style={{ fontSize:12, color:"#374151", fontFamily:"monospace" }}>{evt.date}</span>
                                  {evt.weight > 0 && (
                                    <span style={{ fontSize:11, color:"#6b7280" }}>
                                      {evt.type==="in" ? "初始比重" : "末次比重"} <strong style={{ color:cfg.accent }}>{evt.weight.toFixed(2)}%</strong>
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 今日全覽摘要 ─────────────────────────────────────
function DailySummary({ onNavigate, latestSnap, loading }) {
  const ETF_KEYS = Object.keys(ETF_CONFIG);

  // latestSnap 直接當作 latest 使用
  const latest = latestSnap;

  // 每檔 ETF 今日變動最大前10
  const topMovers = useMemo(() => {
    const out = {};
    ETF_KEYS.forEach(k => {
      const day = latest[k];
      if (!day) { out[k] = []; return; }
      const stocks = [...(day.stocks || [])].filter(s => s.diff != null && s.diff !== 0);
      const byAbs = [...stocks].sort((a,b) => Math.abs(b.diff) - Math.abs(a.diff)).slice(0, 10);
      out[k] = byAbs;
    });
    return out;
  }, [latest]);

  // 跨 ETF 今日最大增加 / 最大減少
  const globalMovers = useMemo(() => {
    const all = [];
    ETF_KEYS.forEach(k => {
      const day = latest[k];
      (day?.stocks || []).filter(s => s.diff != null && s.diff !== 0).forEach(s => {
        all.push({ ...s, etf: k });
      });
    });
    const top5up   = [...all].filter(s => s.diff > 0).sort((a,b) => b.diff - a.diff).slice(0, 5);
    const top5down = [...all].filter(s => s.diff < 0).sort((a,b) => a.diff - b.diff).slice(0, 5);
    const newStocks = all.filter(s => s.is_new);
    return { top5up, top5down, newStocks };
  }, [latest]);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:240 }}>
      <div style={{ width:28, height:28, border:"2px solid #e5e7eb", borderTop:"2px solid #6366f1",
        borderRadius:"50%", animation:"spin .7s linear infinite" }} />
    </div>
  );

  const today = Object.values(latest).find(d => d?.date)?.date || "—";

  return (
    <div>
      {/* 日期標題 */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div>
          <div style={{ fontSize:16, fontWeight:700, color:"#111827" }}>今日全覽</div>
          <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>{today}</div>
        </div>
      </div>

      {/* 4 檔摘要卡 */}
      <div className="summary-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:10, marginBottom:20 }}>
        {ETF_KEYS.map(k => {
          const day = latest[k];
          const cfg = ETF_CONFIG[k];
          const stocks = day?.stocks || [];
          const newCount = stocks.filter(s => s.is_new).length;
          const upCount  = stocks.filter(s => s.diff > 0).length;
          const downCount= stocks.filter(s => s.diff < 0).length;
          const top1 = stocks[0];
          return (
            <div key={k} onClick={() => onNavigate(k)}
              style={{ border:`1px solid ${cfg.accent}25`, borderRadius:10, padding:"12px 14px",
                cursor:"pointer", transition:"box-shadow .15s", background:"white" }}
              onMouseEnter={e => e.currentTarget.style.boxShadow=`0 2px 12px ${cfg.accent}20`}
              onMouseLeave={e => e.currentTarget.style.boxShadow="none"}>
              {/* ETF 名稱 */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                <span style={{ fontSize:14, fontWeight:700, color:cfg.accent }}>{k}</span>
                {day?.nav && <span style={{ fontSize:12, color:"#6b7280" }}>淨值 <strong style={{ color:"#111827" }}>{day.nav}</strong></span>}
              </div>
              {/* 持股數 + 異動 */}
              <div style={{ display:"flex", gap:6, marginBottom:8, flexWrap:"wrap" }}>
                <span style={{ fontSize:11, background:"#f3f4f6", borderRadius:4, padding:"2px 7px", color:"#374151" }}>{stocks.length} 支</span>
                {newCount > 0 && <span style={{ fontSize:11, background:"#eff6ff", borderRadius:4, padding:"2px 7px", color:"#2563eb" }}>新增 {newCount}</span>}
                {upCount  > 0 && <span style={{ fontSize:11, background:"#f0fdf4", borderRadius:4, padding:"2px 7px", color:"#16a34a" }}>▲ {upCount}</span>}
                {downCount> 0 && <span style={{ fontSize:11, background:"#fef2f2", borderRadius:4, padding:"2px 7px", color:"#dc2626" }}>▼ {downCount}</span>}
              </div>
              {/* 最大持股 */}
              {top1 && (
                <div style={{ fontSize:11, color:"#9ca3af" }}>
                  最大持股：<span style={{ color:"#111827", fontWeight:500 }}>{top1.name}</span>
                  <span style={{ color:cfg.accent, fontWeight:600, marginLeft:4 }}>{top1.weight.toFixed(2)}%</span>
                </div>
              )}
              {!day && <div style={{ fontSize:11, color:"#d1d5db" }}>尚無資料</div>}
            </div>
          );
        })}
      </div>

      {/* 跨 ETF 今日最大增加 / 減少 */}
      <div className="daily-movers-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
        {/* 增加 */}
        <div style={{ border:"1px solid #dcfce7", borderRadius:10, overflow:"hidden" }}>
          <div style={{ padding:"10px 14px", background:"#f0fdf4", borderBottom:"1px solid #dcfce7",
            fontSize:12, fontWeight:600, color:"#16a34a" }}>▲ 今日比重增加最多</div>
          {globalMovers.top5up.length === 0
            ? <div style={{ padding:"16px", fontSize:12, color:"#9ca3af", textAlign:"center" }}>無前日資料可比較</div>
            : globalMovers.top5up.map((s, i) => (
            <div key={`${s.etf}-${s.code}`} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px",
              borderTop: i>0 ? "1px solid #f9fafb" : "none" }}>
              <span style={{ fontSize:10, color:"#9ca3af", minWidth:14 }}>{i+1}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <span style={{ fontSize:11, color:"#9ca3af", fontFamily:"monospace", marginRight:4 }}>{s.code}</span>
                <span style={{ fontSize:12, color:"#111827", fontWeight:500 }}>{s.name}</span>
              </div>
              <span style={{ fontSize:10, background:`${ETF_CONFIG[s.etf].accent}15`, color:ETF_CONFIG[s.etf].accent,
                borderRadius:3, padding:"1px 5px", flexShrink:0 }}>{s.etf}</span>
              <span style={{ fontSize:13, fontWeight:700, color:"#16a34a", fontFamily:"monospace", flexShrink:0 }}>
                +{s.diff.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
        {/* 減少 */}
        <div style={{ border:"1px solid #fecaca", borderRadius:10, overflow:"hidden" }}>
          <div style={{ padding:"10px 14px", background:"#fef2f2", borderBottom:"1px solid #fecaca",
            fontSize:12, fontWeight:600, color:"#dc2626" }}>▼ 今日比重減少最多</div>
          {globalMovers.top5down.length === 0
            ? <div style={{ padding:"16px", fontSize:12, color:"#9ca3af", textAlign:"center" }}>無前日資料可比較</div>
            : globalMovers.top5down.map((s, i) => (
            <div key={`${s.etf}-${s.code}`} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px",
              borderTop: i>0 ? "1px solid #f9fafb" : "none" }}>
              <span style={{ fontSize:10, color:"#9ca3af", minWidth:14 }}>{i+1}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <span style={{ fontSize:11, color:"#9ca3af", fontFamily:"monospace", marginRight:4 }}>{s.code}</span>
                <span style={{ fontSize:12, color:"#111827", fontWeight:500 }}>{s.name}</span>
              </div>
              <span style={{ fontSize:10, background:`${ETF_CONFIG[s.etf].accent}15`, color:ETF_CONFIG[s.etf].accent,
                borderRadius:3, padding:"1px 5px", flexShrink:0 }}>{s.etf}</span>
              <span style={{ fontSize:13, fontWeight:700, color:"#dc2626", fontFamily:"monospace", flexShrink:0 }}>
                {s.diff.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 今日新進成分股 */}
      {globalMovers.newStocks.length > 0 && (
        <div style={{ border:"1px solid #dbeafe", borderRadius:10, overflow:"hidden", marginBottom:20 }}>
          <div style={{ padding:"10px 14px", background:"#eff6ff", borderBottom:"1px solid #dbeafe",
            fontSize:12, fontWeight:600, color:"#2563eb" }}>🆕 今日新進成分股</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, padding:12 }}>
            {globalMovers.newStocks.map(s => (
              <div key={`${s.etf}-${s.code}`}
                style={{ border:"1px solid #bfdbfe", borderRadius:7, padding:"6px 11px", background:"white" }}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"monospace" }}>{s.code}
                  <span style={{ marginLeft:4, background:`${ETF_CONFIG[s.etf].accent}15`,
                    color:ETF_CONFIG[s.etf].accent, borderRadius:3, padding:"0 4px" }}>{s.etf}</span>
                </div>
                <div style={{ fontSize:12, fontWeight:600, color:"#111827" }}>{s.name}</div>
                <div style={{ fontSize:12, color:"#2563eb", fontWeight:700, fontFamily:"monospace" }}>{s.weight.toFixed(2)}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 各 ETF Top10 變動 */}
      <div style={{ fontSize:13, fontWeight:600, color:"#374151", marginBottom:10 }}>各 ETF 今日前10大變動</div>
      <div className="daily-etf-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:12 }}>
        {ETF_KEYS.map(k => {
          const cfg = ETF_CONFIG[k];
          const movers = topMovers[k];
          return (
            <div key={k} style={{ border:`1px solid ${cfg.accent}20`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"9px 12px", background:`${cfg.accent}08`, borderBottom:`1px solid ${cfg.accent}15`,
                fontSize:12, fontWeight:700, color:cfg.accent }}>{k}</div>
              {movers.length === 0
                ? <div style={{ padding:"16px", fontSize:12, color:"#9ca3af", textAlign:"center" }}>無前日資料</div>
                : movers.map((s, i) => {
                  const isUp = s.diff > 0;
                  return (
                    <div key={s.code} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 12px",
                      borderTop: i>0 ? "1px solid #f9fafb" : "none" }}>
                      <span style={{ fontSize:10, color:"#d1d5db", minWidth:14 }}>{i+1}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, color:"#111827", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.name}</div>
                        <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"monospace" }}>{s.code} · {s.weight.toFixed(2)}%</div>
                      </div>
                      <span style={{ fontSize:12, fontWeight:700, fontFamily:"monospace", flexShrink:0,
                        color: isUp ? "#16a34a" : "#dc2626" }}>
                        {isUp ? "+" : ""}{s.diff.toFixed(2)}%
                      </span>
                    </div>
                  );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function App() {
  const [activeTab, setActiveTab] = useState("summary");

  // ── 集中式資料快取（Bug 6 修正）────────────────────────
  // 所有頁籤共用同一份 fetch，切換頁籤不再重新載入
  const { allHistory, latestSnap, loading } = useEtfData();

  const TABS = [
    ["summary",  { label:"今日全覽", sublabel:"每日摘要",            accent:"#6366f1" }],
    ...Object.entries(ETF_CONFIG),
    ["overlap",  { label:"重疊分析", sublabel:"4 檔互相比較",        accent:"#6366f1" }],
    ["compare",  { label:"比較模式", sublabel:"左右並排",            accent:"#ec4899" }],
    ["stock",    { label:"個股追蹤", sublabel:"輸入代號查詢",        accent:"#f59e0b" }],
    ["trend",    { label:"趨勢分析", sublabel:"淨值・個股・集中度",  accent:"#14b8a6" }],
    ["history",  { label:"進出歷史", sublabel:"成分股進出紀錄",      accent:"#8b5cf6" }],
    ["industry", { label:"產業分析", sublabel:"分類比重・熱力圖",    accent:"#10b981" }],
  ];

  return (
    <div style={{ background:"white", minHeight:"100vh", color:"#111827", fontFamily:"-apple-system, BlinkMacSystemFont, 'Noto Sans TC', sans-serif" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }

        /* ── RWD：手機版（≤640px） ── */
        @media (max-width: 640px) {

          /* Header */
          .app-header { padding: 10px 14px !important; }
          .app-header h1 { font-size: 13px !important; }

          /* Tab bar */
          .app-tabs { padding: 0 8px !important; }
          .app-tab-btn { padding: 10px 10px !important; font-size: 11px !important; }
          .app-tab-sub { display: none !important; }

          /* Content area */
          .app-content { padding: 14px 12px !important; }

          /* 持股表格 → 卡片式 */
          .holdings-table thead { display: none !important; }
          .holdings-table tbody tr {
            display: flex !important;
            flex-wrap: wrap !important;
            padding: 10px 12px !important;
            border-bottom: 1px solid #f3f4f6 !important;
            gap: 4px 8px !important;
            align-items: center !important;
          }
          .holdings-table td { padding: 0 !important; border: none !important; }
          .td-rank   { order: 0; font-size: 10px; color: #d1d5db; min-width: 16px; }
          .td-code   { order: 1; font-size: 11px; color: #9ca3af; font-family: monospace; }
          .td-name   { order: 2; flex: 1 1 120px; font-size: 13px; font-weight: 500; }
          .td-weight { order: 3; margin-left: auto; font-size: 14px; font-weight: 700; }
          .td-diff   { order: 4; font-size: 11px; width: 100%; padding-left: 24px !important; }
          .td-shares { display: none !important; }

          /* 摘要卡 */
          .summary-grid { grid-template-columns: repeat(2, 1fr) !important; }

          /* Top Movers 卡片 */
          .top-movers-grid { grid-template-columns: 1fr !important; }

          /* 重疊分析表格 */
          .overlap-table thead { display: none !important; }
          .overlap-table tbody tr {
            display: flex !important;
            flex-wrap: wrap !important;
            padding: 10px 12px !important;
            border-bottom: 1px solid #f3f4f6 !important;
            gap: 4px !important;
          }
          .overlap-table td { padding: 0 !important; }
          .overlap-td-name   { flex: 1 1 120px; font-size: 13px; }
          .overlap-td-etf    { font-size: 11px; }
          .overlap-td-total  { margin-left: auto; font-weight: 700; }
          .overlap-td-badge  { width: 100%; }

          /* 比較模式 → 垂直堆疊 */
          .compare-selectors { flex-direction: column !important; }
          .compare-bar-row   { flex-direction: column !important; gap: 4px !important; }
          .compare-bar-left  { flex-direction: row !important; justify-content: flex-start !important; }
          .compare-bar-right { flex-direction: row !important; }

          /* 篩選列 wrapping */
          .filter-row { flex-wrap: wrap !important; gap: 6px !important; }
          .filter-search { width: 100% !important; }

          /* 今日全覽 */
          .daily-movers-grid { grid-template-columns: 1fr !important; }
          .daily-etf-grid    { grid-template-columns: 1fr 1fr !important; }

          /* 產業分析長條圖 label */
          .industry-label { min-width: 60px !important; font-size: 11px !important; }

          /* 個股追蹤 */
          .stock-card-bars { flex-direction: column !important; }

          /* 趨勢分析 */
          .trend-conc-grid { grid-template-columns: 1fr !important; }

          /* footer */
          .app-footer { font-size: 9px !important; padding: 12px !important; }
        }

        @media (max-width: 400px) {
          .daily-etf-grid { grid-template-columns: 1fr !important; }
          .app-tab-btn    { padding: 8px 7px !important; font-size: 10px !important; }
        }
      `}</style>

      {/* Header */}
      <div className="app-header" style={{ borderBottom:"1px solid #f3f4f6", padding:"14px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
        <div>
          <h1 style={{ fontSize:15, fontWeight:600, color:"#111827" }}>ETF 持股比重追蹤</h1>
          <p style={{ fontSize:11, color:"#9ca3af", marginTop:1 }}>00981A × 00403A × 00985A × 0050</p>
        </div>
        <HealthBadge />
      </div>

      {/* Tabs */}
      <div className="app-tabs" style={{ borderBottom:"1px solid #f3f4f6", padding:"0 24px", display:"flex", gap:0, overflowX:"auto" }}>
        {TABS.map(([key, c]) => (
          <button key={key} onClick={() => setActiveTab(key)} className="app-tab-btn"
            style={{ background:"none", border:"none", borderBottom: activeTab===key ? `2px solid ${c.accent}` : "2px solid transparent",
              padding:"12px 16px", fontSize:13, fontWeight: activeTab===key ? 600 : 400,
              color: activeTab===key ? c.accent : "#6b7280", cursor:"pointer", marginBottom:-1,
              transition:"all .15s", whiteSpace:"nowrap" }}>
            {c.label}
            <span className="app-tab-sub" style={{ fontSize:11, color: activeTab===key ? c.accent : "#9ca3af", marginLeft:6, fontWeight:400 }}>{c.sublabel}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="app-content" style={{ padding:"20px 24px", maxWidth:980, margin:"0 auto" }}>
        {activeTab === "summary"  ? <DailySummary  onNavigate={setActiveTab} latestSnap={latestSnap} loading={loading} /> :
         activeTab === "overlap"  ? <OverlapAnalysis  latestSnap={latestSnap} loading={loading} />  :
         activeTab === "compare"  ? <CompareMode       latestSnap={latestSnap} loading={loading} />  :
         activeTab === "stock"    ? <StockTracker      latestSnap={latestSnap} loading={loading} />  :
         activeTab === "trend"    ? <TrendAnalysis     allHistory={allHistory} loading={loading} />  :
         activeTab === "history"  ? <StockHistory      allHistory={allHistory} loading={loading} />  :
         activeTab === "industry" ? <IndustryAnalysis  latestSnap={latestSnap} loading={loading} />  :
         <ETFDashboard key={activeTab} etfKey={activeTab} />}
      </div>

      <div className="app-footer" style={{ textAlign:"center", padding:"16px", fontSize:10, color:"#d1d5db", borderTop:"1px solid #f9fafb" }}>
        資料來源：統一投信・野村投信・元大投信・不構成投資建議
      </div>
    </div>
  );
}
